import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getPositions, placeSquareOff, getMyOrders, getHoldings, getDayPositions, getBFPositions } from '../services/api';
import naviaLogo from '../assets/navia-logo.png';

/* ─── Google Font ────────────────────────────────────────────────────────────*/
const _fl = document.createElement('link');
_fl.rel = 'stylesheet';
_fl.href = 'https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600&family=DM+Mono:wght@400;500&display=swap';
if (!document.head.querySelector('[href*="DM+Sans"]')) document.head.appendChild(_fl);

/* ─── Helpers ────────────────────────────────────────────────────────────────*/
// All API calls use axios instances from ../services/api.js
// Auth (Bearer token) is attached automatically via axios interceptors
async function placeHoldingsSquareOff(payload) {
  // Holdings square-off uses same squareoff endpoint as F&O
  const res = await placeSquareOff(payload);
  return res.data;
}
function formatExpiry(d) {
  if (!d) return '—';
  const dt = new Date(d);
  return isNaN(dt) ? d : dt.toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' });
}
function formatStrike(v) { if (!v && v !== 0) return null; return String(Math.round(Number(v))); }
// "NIFTY 23,900 strike" — no rupee symbol, symbol name + comma-formatted strike + "strike"
function formatStrikeLabel(symbol, strikePrice) {
  const f = formatStrike(strikePrice);
  if (!f) return symbol;
  return `${symbol} ${f}`;
}
function fmtQty(v) { if (v===null||v===undefined) return '0'; const n=Number(v); return Number.isInteger(n)?n.toString():n.toFixed(2); }
// Traded price formatter -- same ₹/en-IN convention used elsewhere in this file
// (holdings close_price/total_value). Returns '—' when no trade price exists
// yet (order not traded) so the column never shows a misleading 0/blank value.
function fmtPrice(v) { if (v===null||v===undefined||v==='') return '—'; return `₹${Number(v).toLocaleString('en-IN',{maximumFractionDigits:2})}`; }
function getInitials(name) { if (!name) return 'C'; return name.split(' ').slice(0,2).map(w=>w[0]).join('').toUpperCase(); }
function useIsMobile() {
  const [m, setM] = useState(window.innerWidth < 768);
  useEffect(() => { const h=()=>setM(window.innerWidth<768); window.addEventListener('resize',h); return ()=>window.removeEventListener('resize',h); }, []);
  return m;
}
function injectCSS(id, css) {
  let el = document.getElementById(id);
  if (!el) { el=document.createElement('style'); el.id=id; document.head.appendChild(el); }
  el.textContent = css;
}

/* ─── Theme system ────────────────────────────────────────────────────────────*/
const THEMES = {
  light: {
    bg:'#F4F6F9', surf:'#FFFFFF', surf2:'#F0F2F5', bdr:'rgba(0,0,0,0.08)', bdr2:'rgba(0,0,0,0.14)',
    tx:'#0D0F12', tx2:'#5A6272', tx3:'#9BA3B0',
    brand:'#1B4FD8', brandL:'#EEF2FF', brandD:'#0C447C', brandH:'#1338A8',
    ok:'#1D9E75', okBg:'#E1F5EE', okTx:'#085041',
    err:'#DC2626', errBg:'#FEF2F2', errTx:'#991B1B',
    warn:'#D97706', warnBg:'#FFFBEB', warnTx:'#92400E',
    buy:'#1B5E20', sell:'#B71C1C', flat:'#6B7280',
    cePill:{ bg:'#E8F5E9', tx:'#1B5E20', bdr:'#A5D6A7' },
    pePill:{ bg:'#FFEBEE', tx:'#B71C1C', bdr:'#FFCDD2' },
    futPill:{ bg:'#E3F2FD', tx:'#0D47A1', bdr:'#90CAF9' },
  },
  dark: {
    bg:'#0B0F1A', surf:'#141928', surf2:'#1C2236', bdr:'rgba(255,255,255,0.08)', bdr2:'rgba(255,255,255,0.15)',
    tx:'#F0F4FF', tx2:'#8B95B0', tx3:'#4B5470',
    brand:'#4F7EF7', brandL:'#1A2460', brandD:'#93B4FF', brandH:'#3B6AE8',
    ok:'#34D399', okBg:'#052E1C', okTx:'#6EE7B7',
    err:'#F87171', errBg:'#2D1010', errTx:'#FCA5A5',
    warn:'#FBBF24', warnBg:'#2D1F00', warnTx:'#FDE68A',
    buy:'#4ADE80', sell:'#F87171', flat:'#6B7280',
    cePill:{ bg:'#052E1C', tx:'#6EE7B7', bdr:'#065F46' },
    pePill:{ bg:'#2D1010', tx:'#FCA5A5', bdr:'#7F1D1D' },
    futPill:{ bg:'#0C1A3A', tx:'#93B4FF', bdr:'#1E3A8A' },
  },
  hc: {
    bg:'#000', surf:'#0A0A0A', surf2:'#111', bdr:'rgba(255,255,255,0.3)', bdr2:'rgba(255,255,255,0.6)',
    tx:'#FFF', tx2:'#CCC', tx3:'#999',
    brand:'#4D9EFF', brandL:'#001A3D', brandD:'#7DBFFF', brandH:'#2277EE',
    ok:'#00FF88', okBg:'#003319', okTx:'#00FF88',
    err:'#FF4444', errBg:'#330000', errTx:'#FF8888',
    warn:'#FFD700', warnBg:'#332700', warnTx:'#FFD700',
    buy:'#00FF88', sell:'#FF4444', flat:'#AAAAAA',
    cePill:{ bg:'#003319', tx:'#00FF88', bdr:'#00AA55' },
    pePill:{ bg:'#330000', tx:'#FF8888', bdr:'#AA0000' },
    futPill:{ bg:'#001A3D', tx:'#7DBFFF', bdr:'#2277EE' },
  }
};

/* ═══════════════════════════════════════════════════════════════════════════*/
export default function Dashboard({ client, onLogout }) {
  /* ── Data state ──────────────────────────────────────────────────────────*/
  const [positions,       setPositions]       = useState([]);
  const [orders,          setOrders]          = useState([]);
  const [bfData,          setBfData]          = useState(null);
  const [dayData,         setDayData]         = useState(null);
  const [holdingsData,    setHoldingsData]    = useState(null);
  const [loading,         setLoading]         = useState(true);
  const [bfLoading,       setBfLoading]       = useState(false);  // don't block on BF
  const [dayLoading,      setDayLoading]      = useState(false);  // don't block on Day
  const [holdingsLoading, setHoldingsLoading] = useState(true);
  const [activeTab,       setActiveTab]       = useState('positions');
  const [netSearch,       setNetSearch]       = useState('');
  const [daySearch,       setDaySearch]       = useState('');
  const [confirmModal,    setConfirmModal]    = useState(null);
  const [sqOffAllModal,   setSqOffAllModal]   = useState(null);  // {positions:[...], type:'fo'|'cm'}
  const [sqOffAllPlacing, setSqOffAllPlacing] = useState(false);
  const [placing,         setPlacing]         = useState(false);
  const [message,         setMessage]         = useState('');
  const [messageType,     setMessageType]     = useState('success');
  const [placedHoldings,  setPlacedHoldings]  = useState({});
  const [placedDayEquity, setPlacedDayEquity] = useState({});
  const [placedFO,        setPlacedFO]        = useState({});  // tracks F&O sq-off placed locally
  const [ssoBlockModal,   setSsoBlockModal]   = useState(false);
  const [showProfile,     setShowProfile]     = useState(false);
  const [showA11y,        setShowA11y]        = useState(false);
  const [showHelp,        setShowHelp]        = useState(false);
  const [profileData,     setProfileData]     = useState(null);  // fetched from /api/auth/profile

  /* ── Accessibility state ─────────────────────────────────────────────────*/
  const [themeKey,      setThemeKey]      = useState(() => localStorage.getItem('nb_theme') || 'light');
  const [fontSize,      setFontSize]      = useState(() => parseInt(localStorage.getItem('nb_fs') || '14'));
  const [reduceMotion,  setReduceMotion]  = useState(() => localStorage.getItem('nb_rm') === '1');
  const [largeTT,       setLargeTT]       = useState(() => localStorage.getItem('nb_ltt') === '1');
  const [focusRing,     setFocusRing]     = useState(() => localStorage.getItem('nb_fr') !== '0');

  const T = THEMES[themeKey];
  const isMobile = useIsMobile();
  const fs = fontSize;

  /* ── Fetch full profile from backend on mount ───────────────────────────*/
  useEffect(() => {
    import('../services/api').then(({ default: axiosApi }) => {
      axiosApi.get('/auth/profile')
        .then(res => { if (res.data?.client) setProfileData(res.data.client); })
        .catch(() => {}); // silent — uses client prop as fallback
    });
  }, []);

  useEffect(() => { localStorage.setItem('nb_theme', themeKey); }, [themeKey]);
  useEffect(() => { localStorage.setItem('nb_fs',    fontSize);  }, [fontSize]);
  useEffect(() => { localStorage.setItem('nb_rm',    reduceMotion?'1':'0'); }, [reduceMotion]);
  useEffect(() => { localStorage.setItem('nb_ltt',   largeTT?'1':'0'); }, [largeTT]);
  useEffect(() => { localStorage.setItem('nb_fr',    focusRing?'1':'0'); }, [focusRing]);

  useEffect(() => {
    const trans = reduceMotion ? 'none' : 'all 0.18s ease';
    const focus = focusRing ? `*:focus-visible{outline:3px solid ${T.brand};outline-offset:2px}` : `*:focus-visible{outline:none}`;
    const btnH  = largeTT ? '48px' : '40px';
    injectCSS('nb-dash-css', `
      *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
      body{font-family:'DM Sans',-apple-system,sans-serif}
      .nb-btn{min-height:${btnH};transition:${trans}}
      ${focus}
      .nb-card-hov:hover{border-color:${T.brand}!important}
      .nb-slide-up{animation:nbSU ${reduceMotion?'0s':'0.2s'} ease}
      @keyframes nbSU{from{transform:translateY(20px);opacity:0}to{transform:translateY(0);opacity:1}}
      .nb-fade{animation:nbFd ${reduceMotion?'0s':'0.15s'} ease}
      @keyframes nbFd{from{opacity:0}to{opacity:1}}
      ::-webkit-scrollbar{width:4px}::-webkit-scrollbar-thumb{background:${T.bdr2};border-radius:99px}
    `);
  }, [T, reduceMotion, focusRing, largeTT]);

  /* ── Data loaders ────────────────────────────────────────────────────────*/
  const firstLoadRef = useRef(true); // track initial load vs background refresh
  const loadData = useCallback(async () => {
    try {
      const [p,o] = await Promise.all([getPositions(), getMyOrders()]);
      const fetchedPositions = p.data.positions || [];
      const fetchedOrders    = o.data.orders    || [];
      setPositions(fetchedPositions);
      setOrders(fetchedOrders);

      // Re-build placedFO from existing orders so button stays disabled after refresh
      // Net positions (day_positions) have no squareoff_placed flag of their own —
      // placedFO is rebuilt entirely from order history (source of truth for button state)
      const foPlaced = {};
      fetchedOrders.forEach(ord => {
        // Only mark as placed for ACTIVE orders (not traded, not admin-only FILE_GENERATED)
        const activeStatuses = ['ORDER_RECEIVED','RECEIVED','PARTIALLY_TRADED'];
        if (activeStatuses.includes((ord.status||'').toUpperCase())) {
          // Include strike/expiry/option_type so a different contract on the same
          // underlying symbol (e.g. a different NIFTY strike) is never incorrectly
          // treated as already having an order placed.
          const k = `${ord.symbol}|${ord.exchange}|${ord.segment||'FO'}|${ord.expiry_date||''}|${ord.strike_price||''}|${ord.option_type||''}`;
          foPlaced[k] = true;
        }
      });
      setPlacedFO(prev => ({ ...prev, ...foPlaced }));
      const holdingsPlaced = {};
      fetchedOrders.forEach(ord => {
        // FIX (14-Jul-2026): FILE_GENERATED was missing here -- once admin
        // generates the RMS file, order.status moves from ORDER_RECEIVED to
        // FILE_GENERATED, so this whitelist stopped matching it and the
        // Holdings Square-off button reverted to clickable, letting the same
        // holding be square-off'd again (same class of bug as the dealer
        // side's isPlaced() gap, fixed the same way here).
        const activeStatuses = ['ORDER_RECEIVED','RECEIVED','FILE_GENERATED','PARTIALLY_TRADED'];
        if ((ord.segment||'').toUpperCase() === 'CM' &&
            (ord.symbol||'').includes(' ; ') &&
            activeStatuses.includes((ord.status||'').toUpperCase())) {
          const isinPart = ord.symbol.split(';').pop().trim().toUpperCase();
          if (isinPart) holdingsPlaced[isinPart] = true;
        }
      });
      setPlacedHoldings(prev => ({ ...prev, ...holdingsPlaced }));
    } catch(e) { console.error(e); }
    setLoading(false);
  },[]);
  const loadBF = useCallback(async () => {
    // Only show spinner on first load — silent refresh afterwards (no blinking)
    if(firstLoadRef.current) setBfLoading(true);
    try {
      const r    = await getBFPositions();
      const raw  = r.data;                          // axios wraps in .data

      // Normalise response — always re-split from the full positions array
      // Backend returns: { success, options:[], futures:[], positions:[ALL] }
      // We always use positions[] to re-split so nothing is missed
      let options = [], futures = [];
      let allPositions = [];

      if (Array.isArray(raw)) {
        allPositions = raw;
      } else if (raw?.positions && Array.isArray(raw.positions)) {
        // Use the full positions array — most reliable source
        allPositions = raw.positions;
      } else if (raw?.data && Array.isArray(raw.data)) {
        allPositions = raw.data;
      } else if (raw?.data?.positions) {
        allPositions = raw.data.positions;
      }

      // Split from full array by instrument_type (case-insensitive)
      if (allPositions.length > 0) {
        options = allPositions.filter(p => (p.instrument_type||'').toUpperCase() === 'OPTIONS');
        futures = allPositions.filter(p => (p.instrument_type||'').toUpperCase() === 'FUTURES');
      } else {
        // Fallback: use pre-split arrays from backend if positions key missing
        options = (raw?.options || []).filter(p => (p.instrument_type||'').toUpperCase() === 'OPTIONS');
        futures = (raw?.futures || []).filter(p => (p.instrument_type||'').toUpperCase() === 'FUTURES');
      }

      // Filter out zero-qty records
      const isNonZero = p => {
        const qty = Number(p.display_qty ?? p.qty_in_lots ?? p.total_open_qty ?? p.opng_lng_qty ?? 0);
        const shrt = Number(p.opng_shrt_qty ?? 0);
        return qty !== 0 || shrt !== 0;
      };
      options = options.filter(isNonZero);
      futures = futures.filter(isNonZero);

      setBfData({
        options,
        futures,
        total_positions: options.length + futures.length,
        total_open_qty:  [...options, ...futures].reduce((s, p) =>
          s + Math.abs(Number(p.display_qty ?? p.qty_in_lots ?? p.total_open_qty ?? p.opng_lng_qty ?? 0)), 0)
      });
    } catch(e) {
      console.error('BF load error:', e);
      setBfData({ options:[], futures:[], total_open_qty:0, total_positions:0 });
    }
    setBfLoading(false);
  },[]);
  const loadHoldings = useCallback(async () => {
    if(firstLoadRef.current) setHoldingsLoading(true); try{const r=await getHoldings();setHoldingsData(r.data);}catch{setHoldingsData({holdings:[],total_holdings:0,total_value:0});} setHoldingsLoading(false);
  },[]);
  const loadDay = useCallback(async () => {
    if(firstLoadRef.current) setDayLoading(true); try{const r=await getDayPositions();setDayData(r.data);}catch{setDayData({positions:[],total:0});} setDayLoading(false);
  },[]);
  useEffect(() => {
    // Initial load — run ALL in parallel so fastest data shows first
    Promise.all([loadData(), loadBF(), loadHoldings(), loadDay()])
      .finally(() => { firstLoadRef.current = false; });
    // Silent background refresh every 60s — no spinners, no blinking
    const iv = setInterval(() => { loadData(); loadBF(); loadHoldings(); loadDay(); }, 90000);
    return () => clearInterval(iv);
  },[loadData,loadBF,loadHoldings,loadDay]);

  /* ── Net position merge ──────────────────────────────────────────────────*/
  const dayPositions = dayData?.positions||[];
  const dayEquity    = dayPositions.filter(p=>p.instrument_type==='EQUITY');
  const dayOptions   = dayPositions.filter(p=>p.instrument_type==='OPTIONS');
  const dayFutures   = dayPositions.filter(p=>p.instrument_type==='FUTURES');
  const dayTotalNet  = dayPositions.length;
  const bfOptions    = bfData?.options||[];
  const bfFutures    = bfData?.futures||[];

  const mergeKey = (sym,exp,str,opt) => `${sym}|${(exp||'').slice(0,10)}|${str||''}|${opt||''}`;
  const netMap = new Map();
  ;[...bfOptions,...bfFutures].forEach(pos=>{
    const key=mergeKey(pos.symbol,pos.expiry_date,pos.strike_price,pos.option_type);
    // Support all qty field names from backend
    const rawNet = Number(pos.display_qty ?? pos.qty_in_lots ?? pos.total_open_qty ?? 0);
    const lngQty = Number(pos.opng_lng_qty  ?? 0);
    const shrtQty= Number(pos.opng_shrt_qty ?? 0);
    // Use opng_lng/shrt if explicit, otherwise interpret rawNet as long
    const bfBuy  = lngQty  > 0 ? lngQty  : (rawNet > 0 ? rawNet  : 0);
    const bfSell = shrtQty > 0 ? shrtQty : (rawNet < 0 ? -rawNet : 0);
    if(!netMap.has(key)) netMap.set(key,{symbol:pos.symbol,exchange:pos.exchange,segment:pos.segment||'FO',expiry_date:pos.expiry_date,strike_price:pos.strike_price,option_type:pos.option_type,instrument_type:pos.instrument_type,total_buy:bfBuy,total_sell:bfSell});
    else{const e=netMap.get(key);e.total_buy+=bfBuy;e.total_sell+=bfSell;}
  });
  ;[...dayOptions,...dayFutures].forEach(pos=>{
    const key=mergeKey(pos.symbol,pos.expiry_date,pos.strike_price,pos.option_type);
    const dBuy=Number(pos.buy_qty)||0; const dSell=Number(pos.sell_qty)||0;
    if(!netMap.has(key)) netMap.set(key,{symbol:pos.symbol,exchange:pos.exchange,segment:pos.segment||'FO',expiry_date:pos.expiry_date,strike_price:pos.strike_price,option_type:pos.option_type,instrument_type:pos.instrument_type,total_buy:dBuy,total_sell:dSell});
    else{const e=netMap.get(key);e.total_buy+=dBuy;e.total_sell+=dSell;}
  });
  const netFOPositions = Array.from(netMap.values())
    .map(p=>({...p,net_qty:p.total_buy-p.total_sell}))
    .filter(p=>p.total_buy>0||p.total_sell>0);

  const netCount=netFOPositions.length;
  const bfCount=(bfData?.options?.length||0)+(bfData?.futures?.length||0);
  const holdingsCount=holdingsData?.total_holdings??0;

  /* ── Sq-off handlers ─────────────────────────────────────────────────────*/
  // Classify a holding as squareoff-eligible plain equity vs everything else
  // (bonds, SGBs, ETFs, InvIT/REIT units, mutual funds).
  const isEquityHolding = (compname, isin) => {
    const isinU = (isin || '').toUpperCase().trim();
    const nameU = (compname || '').toUpperCase();
    if (isinU.startsWith('INF')) return false;
    if (isinU.startsWith('IN0')) return false;
    const looksEquity    = /\bEQ\.?\b/.test(nameU);
    const looksNonEquity = /MUTUAL FUND|\bETF\b|\bINVIT\b|\bTRUST\b|\bBD\b|\bBOND\b|\bSGB\b|\bT-BILL\b|\bSDL\b/.test(nameU);
    return looksEquity && !looksNonEquity;
  };
  const handleSquareOff      = p => setConfirmModal({...p,side:p.net_qty>0?'SELL':'BUY',quantity:Math.abs(p.net_qty),isHolding:false,isDayEquity:false});
  // Restored -- referenced by holdingsActionFor below but its definition had
  // been lost/overwritten; shape matches confirmSquareOff/setPlacedHoldings'
  // expected confirmModal fields (symbol, isin, displayName, isHolding) exactly.
  const handleHoldingSquareOff = h => setConfirmModal({symbol:`${h.company_name} ; ${h.isin}`,exchange:'NSE',segment:'CM',side:'SELL',quantity:h.quantity,displayName:`${h.company_name} ; ${h.isin}`,isin:h.isin,isHolding:true});
  const handleDayEquitySquareOff = p => setConfirmModal({symbol:p.symbol,exchange:p.exchange,segment:p.segment,side:Number(p.net_qty)>0?'SELL':'BUY',quantity:Math.abs(Number(p.net_qty)),isHolding:false,isDayEquity:true,dayKey:p.symbol+'_'+p.exchange+'_'+p.segment});
  const handleFOSquareOff    = p => setConfirmModal({symbol:p.symbol,exchange:p.exchange,segment:p.segment||'FO',side:Number(p.net_qty)>0?'SELL':'BUY',quantity:Math.abs(Number(p.net_qty)),isHolding:false,isDayEquity:false,foKey:`${p.symbol}|${p.exchange}|${p.segment||'FO'}|${p.expiry_date||''}|${p.strike_price||''}|${p.option_type||''}`,expiry_date:p.expiry_date||null,strike_price:p.strike_price||null,option_type:p.option_type||null});

  // ── Square Off All ────────────────────────────────────────────────────────
  // Opens a confirmation modal showing ALL currently open (non-flat, no existing
  // file-generated order) positions for the given type (F&O or CM equity).
  // FIX (2026-09-02): the eligibility check below used to be a standalone
  // "has ANY order today for this symbol" test -- (a) for F&O it matched on
  // symbol+exchange+segment only, missing expiry_date/strike_price/option_type,
  // so a client with an order today on one contract (e.g. NIFTY 22000 PE)
  // could have an entirely different, still-open contract on the same
  // underlying (e.g. NIFTY 23000 PE) wrongly excluded too; (b) for both F&O
  // and CM it treated ANY status (including REJECTED/FAILED, or an already-
  // resolved earlier round-trip today) as blocking, even though the very
  // same case is explicitly handled as non-blocking by foActionFor's and
  // dayEquityActionFor's own comments just above ("a contract can have an
  // earlier, genuinely-resolved round-trip today (Traded) and STILL have
  // brand-new open exposure right now"). Net effect: "Square off all" could
  // silently compute zero eligible positions and show "No eligible open
  // positions to square off" while individual rows still showed a live,
  // clickable "Square off" button -- exactly the mismatch reported.
  //
  // Fix: mirror the EXACT contract-matching and blocking-status rules
  // foActionFor/dayEquityActionFor already use to decide the per-row button,
  // so a position is bulk-eligible if and only if its own row currently shows
  // an actionable "Square off" button. foActionFor/dayEquityActionFor
  // themselves are untouched -- this only changes which positions get
  // grouped into the bulk-confirm modal.
  const handleSqOffAll = (positions, type) => {
    const todayIST  = new Date(Date.now() + 5.5*60*60*1000).toISOString().slice(0,10);
    const placedIST = o => o.placed_at
        ? new Date(new Date(o.placed_at).getTime() + 5.5*60*60*1000).toISOString().slice(0,10)
        : '';
    const isCM = type === 'cm';

    const eligible = positions.filter(pos => {
      const netQty = Number(pos.net_qty);
      if (netQty === 0) return false;

      if (isCM) {
        // Mirrors dayEquityActionFor's exact match + blocking statuses.
        const todayOrder = clientVisibleOrders.find(o =>
            o.symbol   === pos.symbol &&
            o.exchange === pos.exchange &&
            o.segment  === pos.segment &&
            placedIST(o) === todayIST
        );
        const ordSt = (todayOrder?.status||'').toUpperCase();
        if (todayOrder && !!todayOrder.file_generated && ordSt==='PARTIALLY_TRADED') return false;
        if (ordSt==='ORDER_RECEIVED'||ordSt==='RECEIVED'||ordSt==='FILE_GENERATED') return false;
        const dk = pos.symbol+'_'+pos.exchange+'_'+pos.segment;
        if (placedDayEquity[dk]) return false;
        return true;
      }

      // Mirrors foActionFor's exact FULL contract match + blocking statuses.
      const contractMatch = o =>
          o.symbol   === pos.symbol &&
          o.exchange === pos.exchange &&
          (o.segment||'FO') === (pos.segment||'FO') &&
          (o.expiry_date ? o.expiry_date.slice(0,10) : null) === (pos.expiry_date ? pos.expiry_date.slice(0,10) : null) &&
          Number(o.strike_price||0) === Number(pos.strike_price||0) &&
          (o.option_type||null) === (pos.option_type||null);
      const todayOrder = clientVisibleOrders.find(o => contractMatch(o) && placedIST(o) === todayIST);
      const ordSt = (todayOrder?.status||'').toUpperCase();
      if (todayOrder && !!todayOrder.file_generated && ordSt==='PARTIALLY_TRADED') return false;
      if (ordSt==='ORDER_RECEIVED'||ordSt==='RECEIVED'||ordSt==='FILE_GENERATED') return false;
      const key = `${pos.symbol}|${pos.exchange}|${pos.segment||'FO'}|${pos.expiry_date||''}|${pos.strike_price||''}|${pos.option_type||''}`;
      if (placedFO[key] || pos.squareoff_placed) return false;
      return true;
    });

    if (!eligible.length) {
      setMessage('No eligible open positions to square off.');
      setMessageType('error');
      // FIX (2026-09-02): this message previously had no auto-clear timeout
      // (every other setMessage call in this file clears after 6s), so once
      // shown it stuck on screen indefinitely across tab switches and
      // refreshes, making a one-off "nothing eligible right now" look like a
      // permanent, unrelated banner.
      setTimeout(()=>setMessage(''),6000);
      return;
    }
    setSqOffAllModal({ positions: eligible, type });
  };

  // Places individual squareoff orders for every position in sqOffAllModal
  const confirmSqOffAll = async () => {
    if (!sqOffAllModal?.positions?.length) return;
    setSqOffAllPlacing(true);
    const results = { ok: 0, fail: 0 };
    for (const pos of sqOffAllModal.positions) {
      try {
        const isCM  = sqOffAllModal.type === 'cm';
        const side  = Number(pos.net_qty) > 0 ? 'SELL' : 'BUY';
        const qty   = Math.abs(Number(pos.net_qty));
        await placeSquareOff({
          exchange:     pos.exchange,
          segment:      isCM ? 'CM' : (pos.segment || 'FO'),
          symbol:       pos.symbol,
          quantity:     qty,
          side,
          expiry_date:  pos.expiry_date  || null,
          strike_price: pos.strike_price || null,
          option_type:  pos.option_type  || null,
        });
        // Mark in local state so button reflects immediately
        if (isCM) {
          const dk = pos.symbol + '_' + pos.exchange + '_' + (pos.segment || 'CM');
          setPlacedDayEquity(p => ({ ...p, [dk]: true }));
        } else {
          const foKey = `${pos.symbol}|${pos.exchange}|${pos.segment||'FO'}|${pos.expiry_date||''}|${pos.strike_price||''}|${pos.option_type||''}`;
          setPlacedFO(p => ({ ...p, [foKey]: true }));
        }
        results.ok++;
      } catch {
        results.fail++;
      }
    }
    setSqOffAllPlacing(false);
    setSqOffAllModal(null);
    if (results.fail === 0) {
      setMessage(`Square-off placed for all ${results.ok} position${results.ok>1?'s':''}. RMS team notified.`);
      setMessageType('success');
    } else {
      setMessage(`${results.ok} placed, ${results.fail} failed. Check positions and retry.`);
      setMessageType('error');
    }
    setConfirmModal(null);
    setTimeout(() => getMyOrders().then(o => setOrders(o.data.orders||[])).catch(()=>{}), 800);
  };

  const confirmSquareOff = async () => {
    setPlacing(true);
    try {
      if(confirmModal.isHolding){
        await placeHoldingsSquareOff({exchange:confirmModal.exchange,segment:confirmModal.segment,symbol:confirmModal.symbol,quantity:confirmModal.quantity,side:'SELL'});
        setPlacedHoldings(p=>({...p,[confirmModal.isin]:true}));
        // Also mark in placedFO so holdings button disables immediately
        const hKey = `${confirmModal.symbol}|${confirmModal.exchange}|${confirmModal.segment}`;
        setPlacedFO(p=>({...p,[hKey]:true}));
        setMessage(`Square-off placed for ${confirmModal.displayName}. RMS team notified.`);
      } else {
        await placeSquareOff({
          exchange:     confirmModal.exchange,
          segment:      confirmModal.segment,
          symbol:       confirmModal.symbol,
          quantity:     confirmModal.quantity,
          side:         confirmModal.side,
          expiry_date:  confirmModal.expiry_date  || null,
          strike_price: confirmModal.strike_price || null,
          option_type:  confirmModal.option_type  || null,
        });
        if(confirmModal.isDayEquity) {
          setPlacedDayEquity(p=>({...p,[confirmModal.dayKey]:true}));
        } else {
          // Mark F&O net position as placed so button disables immediately
          // Use confirmModal.foKey directly — it already includes the full
          // contract identity (expiry/strike/option_type), don't rebuild it
          // here without those fields or this fix is silently undone.
          const foKey = confirmModal.foKey || `${confirmModal.symbol}|${confirmModal.exchange}|${confirmModal.segment}`;
          setPlacedFO(p=>({...p,[foKey]:true}));
        }
        setMessage(`Square-off placed for ${confirmModal.symbol}. RMS team notified.`);
        // Refresh only orders in background — full loadData() causes tab blink
        getMyOrders().then(o => setOrders(o.data.orders||[])).catch(()=>{});
      }
      setMessageType('success'); setConfirmModal(null);
    } catch(err) {
      // ── FIX: axios wraps backend errors in err.response.data
      // err.message = "Request failed with status code 403" (generic axios)
      // err.response.data.error = "SEGMENT_DISABLED" (actual backend error code)
      // Must check backend payload first, not axios generic message
      const backendError   = err.response?.data?.error   || '';
      const backendMessage = err.response?.data?.message  || '';
      const axiosMsg       = err.message || '';

      const isBlocked =
        backendError.includes('SEGMENT_DISABLED')  ||
        backendError.includes('SSO_BLOCKED')        ||
        backendMessage.includes('trading application is functioning') ||
        axiosMsg.includes('trading application is functioning');

      // Duplicate order — show clear message, don't show ssoBlockModal
      if (backendError.includes('DUPLICATE_ORDER') || backendError.includes('already been placed')) {
        setMessage('A square-off order already exists for this security.');
        setMessageType('error'); setConfirmModal(null);
        setPlacing(false); return;
      }

      if(isBlocked){
        setConfirmModal(null); setPlacing(false); setSsoBlockModal(true); return;
      }

      // Show meaningful error — prefer backend message over axios generic
      const displayMsg = backendMessage || backendError || axiosMsg || 'Failed to place order. Please try again.';
      setMessage(displayMsg); setMessageType('error'); setConfirmModal(null);
    }
    setPlacing(false);
    setTimeout(()=>setMessage(''),6000);
  };

  /* ── Style helpers ────────────────────────────────────────────────────────*/
  const btnH = largeTT ? 48 : 40;
  const s = {
    page:      { minHeight:'100vh', background:T.bg, fontFamily:"'DM Sans',-apple-system,sans-serif", fontSize:fs, color:T.tx },
    topBar:    { background:T.surf, borderBottom:`0.5px solid ${T.bdr}`, display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0 24px', height:54, position:'sticky', top:0, zIndex:50 },
    logoImg:   { height:28, width:'auto', objectFit:'contain' },
    brandName: { fontSize:13, fontWeight:600, color:T.tx },
    brandSub:  { fontSize:10, color:T.tx3 },
    iconBtn:   { width:largeTT?40:32, height:largeTT?40:32, background:T.surf2, border:`0.5px solid ${T.bdr}`, borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', color:T.tx2, flexShrink:0 },
    avatarBtn: { width:largeTT?40:34, height:largeTT?40:34, background:T.brandL, border:`1.5px solid ${T.brand}`, borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', fontSize:12, fontWeight:600, color:T.brand, flexShrink:0 },
    alertBar:  { background:T.warnBg, borderBottom:`0.5px solid ${T.warn}`, padding:'9px 24px', fontSize:fs-2, color:T.warnTx, display:'flex', alignItems:'center', gap:8 },
    body:      { padding:isMobile?14:20, paddingLeft:isMobile?14:24, paddingRight:isMobile?14:24 },
    statGrid:  { display:'grid', gridTemplateColumns:isMobile?'repeat(2,1fr)':'repeat(5,1fr)', gap:10, marginBottom:16 },
    statCard:  (sel) => ({ background:sel?T.brandL:T.surf, border:`0.5px solid ${sel?T.brand:T.bdr}`, borderRadius:12, padding:'13px 15px', cursor:'pointer', transition:'border-color .15s' }),
    statTag:   (bg,col) => ({ display:'inline-block', fontSize:9, fontWeight:600, letterSpacing:'.05em', padding:'2px 7px', borderRadius:4, marginBottom:7, background:bg, color:col }),
    statNum:   (sel) => ({ fontSize:24, fontWeight:500, lineHeight:1, color:sel?T.brand:T.tx }),
    statLbl:   { fontSize:11, color:T.tx2, marginTop:3 },
    tabRow:    { display:'flex', background:T.surf, border:`0.5px solid ${T.bdr}`, borderRadius:8, overflow:'hidden', marginBottom:16 },
    tabPill:   (act,last) => ({ flex:1, border:'none', background:act?T.brand:'transparent', padding:`10px ${isMobile?'4':'8'}px`, fontSize:isMobile?10:12, fontWeight:500, cursor:'pointer', fontFamily:"'DM Sans',sans-serif", color:act?'#fff':T.tx2, borderRight:last?'none':`0.5px solid ${T.bdr}`, transition:'.15s' }),
    tcard:     { background:T.surf, border:`0.5px solid ${T.bdr}`, borderRadius:12, overflow:'hidden', marginBottom:14 },
    th:        { background:T.surf2, borderBottom:`0.5px solid ${T.bdr}`, color:T.tx2, fontSize:10, fontWeight:600, padding:'9px 13px', textAlign:'left', textTransform:'uppercase', letterSpacing:'.04em', whiteSpace:'nowrap' },
    td:        { color:T.tx, fontSize:fs-1, padding:'11px 13px', verticalAlign:'middle', borderBottom:`0.5px solid ${T.bdr}` },
    sqBtn:     (sm) => ({ background:T.err, color:'#fff', border:'none', borderRadius:sm?6:8, padding:sm?'6px 12px':'11px', fontSize:sm?12:fs-1, fontWeight:500, cursor:'pointer', fontFamily:"'DM Sans',sans-serif", display:'flex', alignItems:'center', justifyContent:'center', gap:6, width:sm?'auto':'100%', minHeight:sm?32:btnH, transition:'opacity .15s' }),
    flatDisabled: { background:T.surf2, color:T.tx3, border:`0.5px solid ${T.bdr}`, borderRadius:6, padding:'6px 12px', fontSize:12, cursor:'not-allowed', fontFamily:"'DM Sans',sans-serif", whiteSpace:'nowrap', display:'inline-block', minWidth:110, textAlign:'center' },
    exchBadge: (exch) => {
      const m={NSE:{bg:T.brandL,col:T.brandD},BSE:{bg:T.warnBg,col:T.warnTx},MCX:{bg:'#EEEDFE',col:'#3C3489'}};
      const c=m[exch]||m.NSE;
      return {fontSize:9,fontWeight:600,padding:'2px 6px',borderRadius:4,background:c.bg,color:c.col};
    },
    typePill:  (opt) => {
      if(opt==='CE') return {background:T.cePill.bg,color:T.cePill.tx,border:`0.5px solid ${T.cePill.bdr}`,borderRadius:4,fontSize:10,fontWeight:600,padding:'3px 8px'};
      if(opt==='PE') return {background:T.pePill.bg,color:T.pePill.tx,border:`0.5px solid ${T.pePill.bdr}`,borderRadius:4,fontSize:10,fontWeight:600,padding:'3px 8px'};
      if(opt==='EQ') return {background:T.surf2,color:T.tx2,border:`0.5px solid ${T.bdr}`,borderRadius:4,fontSize:10,fontWeight:600,padding:'3px 8px'};
      return {background:T.futPill.bg,color:T.futPill.tx,border:`0.5px solid ${T.futPill.bdr}`,borderRadius:4,fontSize:10,fontWeight:600,padding:'3px 8px'};
    },
    sumBar:    { background:T.surf, border:`0.5px solid ${T.bdr}`, borderRadius:'12px 12px 0 0', display:'flex', flexWrap:'wrap', borderBottom:`0.5px solid ${T.bdr}` },
    sumItem:   { padding:'11px 16px', display:'flex', flexDirection:'column', gap:2, borderRight:`0.5px solid ${T.bdr}` },
    sumLbl:    { fontSize:9, fontWeight:600, color:T.tx3, textTransform:'uppercase', letterSpacing:'.05em' },
    sumVal:    (col) => ({ fontSize:19, fontWeight:500, color:col||T.tx }),
    vonly:     { background:T.warnBg, border:`0.5px solid ${T.warn}`, color:T.warnTx, borderRadius:6, fontSize:11, fontWeight:500, padding:'6px 12px', whiteSpace:'nowrap', display:'flex', alignItems:'center', margin:'auto 0' },
    overlay:   { position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, padding:16 },
    modal:     { background:T.surf, borderRadius:16, width:'100%', maxWidth:440, border:`0.5px solid ${T.bdr2}`, display:'flex', flexDirection:'column' },
    drawer:    { position:'fixed', bottom:0, left:'50%', transform:'translateX(-50%)', width:'min(500px,100%)', background:T.surf, borderRadius:'20px 20px 0 0', border:`0.5px solid ${T.bdr2}`, zIndex:201, maxHeight:'85vh', overflowY:'auto' },
    secHdr:    { display:'flex', alignItems:'center', gap:8, margin:'0 0 10px' },
    secTtl:    { fontSize:11, fontWeight:600, textTransform:'uppercase', letterSpacing:'.08em', color:T.tx2 },
    secBdg:    { fontSize:10, fontWeight:600, padding:'2px 8px', borderRadius:4, background:T.brandL, color:T.brandD },
    secCnt:    { background:T.surf2, color:T.tx2, fontSize:10, padding:'2px 7px', borderRadius:10, fontWeight:600 },
    secLn:     { flex:1, height:1, background:T.bdr },
    emptyState:{ color:T.tx3, fontSize:fs, padding:48, textAlign:'center' },
    msgBanner: { margin:'10px 24px 0', borderRadius:8, padding:'10px 14px', fontSize:fs-1 },
    // Universal pill size: Order received / Traded / Partially traded / No open
    // position all now render at a consistent fixed width instead of auto-sizing
    // to their own text length.
    placedBdg: { background:T.warnBg, border:`0.5px solid ${T.warn}`, borderRadius:6, color:T.warnTx, fontSize:10, fontWeight:500, padding:'4px 10px', whiteSpace:'nowrap', display:'inline-block', minWidth:110, textAlign:'center' },
    tog:       (on) => ({ width:42, height:24, borderRadius:99, position:'relative', cursor:'pointer', background:on?T.brand:T.bdr2, flexShrink:0, border:'none', transition:'background .2s' }),
    togThumb:  (on) => ({ width:18, height:18, background:'#fff', borderRadius:'50%', position:'absolute', top:3, left:on?21:3, transition:'left .18s' }),
    footer:    { color:T.tx3, fontSize:11, textAlign:'center', padding:'18px 0 24px', lineHeight:1.7 },
    footerLnk: { color:T.brand, textDecoration:'none' },
    // ── Mobile card styles (used only when isMobile === true; desktop tables unaffected) ──
    mCard:     { background:T.surf, border:`0.5px solid ${T.bdr}`, borderRadius:12, padding:'13px 14px', marginBottom:10 },
    mCardTop:  { display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:10, marginBottom:9 },
    mCardSym:  { fontSize:15, fontWeight:600, color:T.tx, lineHeight:1.15 },
    mCardSub:  { fontSize:11, color:T.tx2, marginTop:2 },
    mBadges:   { display:'flex', gap:5, flexWrap:'wrap', marginBottom:10 },
    mExpBadge: { background:T.surf2, color:T.tx2, borderRadius:4, fontSize:10, fontWeight:600, padding:'3px 8px' },
    qtyRow:    { display:'flex', borderTop:`0.5px solid ${T.bdr}`, paddingTop:9 },
    qtyCell:   (showDivider) => ({ flex:1, textAlign:'center', borderLeft: showDivider?`0.5px solid ${T.bdr}`:'none' }),
    qtyLbl:    { fontSize:9, color:T.tx3, fontWeight:600, textTransform:'uppercase', letterSpacing:'.04em', marginBottom:3 },
    qtyVal:    { fontSize:14, fontWeight:500, color:T.tx },
    mHoldMeta: { display:'flex', gap:14, marginTop:6 },
    mHoldCell: { display:'flex', flexDirection:'column', gap:1 },
    mHoldLbl:  { fontSize:9, color:T.tx3, fontWeight:600, textTransform:'uppercase', letterSpacing:'.04em' },
    mHoldVal:  { fontSize:13, fontWeight:500, color:T.tx },
    mOrdCard:  { background:T.surf, border:`0.5px solid ${T.bdr}`, borderRadius:12, padding:'12px 13px', marginBottom:8, display:'flex', alignItems:'center', gap:10 },
    mOrdIcon:  (side) => ({ width:32, height:32, background:side==='BUY'?T.okBg:T.errBg, color:side==='BUY'?T.ok:T.err, borderRadius:8, display:'flex', alignItems:'center', justifyContent:'center', fontSize:10, fontWeight:700, flexShrink:0 }),
  };

  /* ── Reusable atoms ──────────────────────────────────────────────────────*/
  const Toggle = ({on, onToggle, label}) => (
    <div role="switch" aria-checked={on} aria-label={label} tabIndex={0}
      style={s.tog(on)} onClick={onToggle}
      onKeyDown={e=>(e.key==='Enter'||e.key===' ')&&onToggle()}>
      <div style={s.togThumb(on)}/>
    </div>
  );
  const QtyDisplay = ({v}) => {
    const n=Number(v);
    if(n>0) return <span style={{color:T.ok,fontWeight:500}}>+{fmtQty(n)}</span>;
    if(n<0) return <span style={{color:T.err,fontWeight:500}}>{fmtQty(n)}</span>;
    return <span style={{color:T.flat}}>0</span>;
  };
  const ExchBadge = ({exchange,segment}) => <span style={s.exchBadge(exchange)}>{exchange}{segment?` ${segment}`:''}</span>;
  const SectionHdr = ({title,badge,count}) => (
    <div style={s.secHdr}>
      <span style={s.secTtl}>{title}</span>
      {badge && <span style={s.secBdg}>{badge}</span>}
      <span style={s.secCnt}>{count}</span>
      <div style={s.secLn}/>
    </div>
  );

  // ── PWDBanner — the existing top warning strip, extracted into a named component.
  // Text/behaviour is unchanged from the original inline <div style={s.alertBar}>.
  const PWDBanner = () => (
    <div style={s.alertBar} role="alert">
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{flexShrink:0}}><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
      Market orders only &nbsp;·&nbsp; Square-off once per security &nbsp;·&nbsp; Trades will not reflect in main app immediately &nbsp;·&nbsp; Do not repeat trades when main platform resumes
    </div>
  );

  // ── MobileFOCard — replaces the horizontally-scrolling table row on mobile for
  // any F&O / equity position (Net, Day, B/F tabs). `action` is a fully-rendered
  // node (button/pill) so business logic for what action to show stays in one
  // place (foActionFor / dayEquityActionFor below) instead of being duplicated.
  const MobileFOCard = ({symbol, strikePrice, expiryDate, optionType, exchange, segment, buyQty, sellQty, netQty, action}) => (
    <div style={s.mCard} className="nb-fade">
      <div style={s.mCardTop}>
        <div>
          <div style={s.mCardSym}>{symbol}</div>
          {(strikePrice!==undefined && strikePrice!==null && strikePrice!=='') &&
            <div style={s.mCardSub}>{formatStrikeLabel(symbol, strikePrice)}</div>}
        </div>
        {action}
      </div>
      <div style={s.mBadges}>
        <ExchBadge exchange={exchange} segment={segment}/>
        {optionType && <span style={s.typePill(optionType)}>{optionType}</span>}
        {expiryDate && <span style={s.mExpBadge}>{formatExpiry(expiryDate)}</span>}
      </div>
      <div style={s.qtyRow}>
        <div style={s.qtyCell(false)}><div style={s.qtyLbl}>Buy</div><div style={{...s.qtyVal,color:T.buy}}>{fmtQty(buyQty)}</div></div>
        <div style={s.qtyCell(true)}><div style={s.qtyLbl}>Sell</div><div style={{...s.qtyVal,color:T.sell}}>{fmtQty(sellQty)}</div></div>
        <div style={s.qtyCell(true)}><div style={s.qtyLbl}>Net</div><div style={s.qtyVal}><QtyDisplay v={netQty}/></div></div>
      </div>
    </div>
  );

  // ── MobileHoldingsCard — Holdings tab card (company name, qty/close-price/value, action)
  const MobileHoldingsCard = ({h, action}) => (
    <div style={s.mCard} className="nb-fade">
      <div style={s.mCardTop}>
        <div style={{...s.mCardSym, fontSize:13, maxWidth:200}}>{h.company_name}</div>
        {action}
      </div>
      <div style={s.mHoldMeta}>
        <div style={s.mHoldCell}><div style={s.mHoldLbl}>Qty</div><div style={s.mHoldVal}>{h.quantity}</div></div>
        <div style={s.mHoldCell}><div style={s.mHoldLbl}>Close price</div><div style={s.mHoldVal}>₹{Number(h.close_price).toLocaleString('en-IN',{maximumFractionDigits:2})}</div></div>
        <div style={s.mHoldCell}><div style={s.mHoldLbl}>Value</div><div style={{...s.mHoldVal,color:T.ok,fontWeight:600}}>₹{Number(h.total_value).toLocaleString('en-IN',{maximumFractionDigits:0})}</div></div>
      </div>
    </div>
  );

  // ── MobileOrderCard — Orders tab card (side icon, symbol/sub info, status pill)
  const MobileOrderCard = ({ord, label}) => {
    const placedByLabel = (ord.placed_by||'CLIENT') + (ord.dealer_id ? ` (${ord.dealer_id})` : '');
    const placedAtFmt = ord.placed_at ? new Date(ord.placed_at).toLocaleString('en-IN',{day:'2-digit',month:'short',hour:'2-digit',minute:'2-digit',hour12:true}) : '—';
    const tradedTimeFmt = ord.traded_at ? new Date(ord.traded_at).toLocaleTimeString('en-IN',{hour:'2-digit',minute:'2-digit',second:'2-digit',hour12:true}) : null;
    return (
    <div style={s.mOrdCard} className="nb-fade">
      <div style={s.mOrdIcon(ord.side)}>{ord.side}</div>
      <div style={{flex:1, minWidth:0}}>
        <div style={{fontSize:13,fontWeight:600,color:T.tx}}>
          {ord.strike_price ? formatStrikeLabel(ord.symbol, ord.strike_price) : ord.symbol}
          {ord.strike_price && ord.option_type ? ` ${ord.option_type}` : ''}
        </div>
        <div style={{fontSize:10,color:T.tx3,marginTop:1}}>
          {ord.exchange}{ord.segment?` ${ord.segment}`:''} · Placed qty: {ord.quantity} · Market
          {ord.expiry_date && ` · ${new Date(ord.expiry_date).toLocaleDateString('en-IN',{day:'2-digit',month:'short'})}`}
        </div>
        <div style={{fontSize:10,color:T.tx3,marginTop:1}}>
          By: {placedByLabel} · {placedAtFmt}
        </div>
        {tradedTimeFmt && (
          <div style={{fontSize:10,color:T.ok,marginTop:1}}>
            Traded qty: {ord.executed_qty ?? '—'} · {tradedTimeFmt}{ord.trade_price!=null ? ` · ${fmtPrice(ord.trade_price)}` : ''}
          </div>
        )}
      </div>
      <span style={{background:label.bg,color:label.col,border:`0.5px solid ${label.col}`,borderRadius:6,fontSize:9,fontWeight:600,padding:'3px 8px',whiteSpace:'nowrap',flexShrink:0}}>{label.label}</span>
    </div>
  );};

  // ── Shared action renderers — single source of truth for desktop table cells
  // AND mobile cards, so the two views can never show different button states.
  const foActionFor = (pos) => {
    const netQty = Number(pos.net_qty);
    const isFlat = netQty === 0;
    const key = `${pos.symbol}|${pos.exchange}|${pos.segment||'FO'}|${pos.expiry_date||''}|${pos.strike_price||''}|${pos.option_type||''}`;
    // TODAY in IST -- placed_at from server is UTC so convert before comparing
    const todayIST = new Date(Date.now() + 5.5*60*60*1000).toISOString().slice(0,10);
    const placedIST = o => o.placed_at
        ? new Date(new Date(o.placed_at).getTime() + 5.5*60*60*1000).toISOString().slice(0,10)
        : '';
    // Contract match (symbol + exchange + segment + expiry + strike + optiontype)
    const contractMatch = o =>
        o.symbol   === pos.symbol &&
        o.exchange === pos.exchange &&
        (o.segment||'FO') === (pos.segment||'FO') &&
        (o.expiry_date ? o.expiry_date.slice(0,10) : null) === (pos.expiry_date ? pos.expiry_date.slice(0,10) : null) &&
        Number(o.strike_price||0) === Number(pos.strike_price||0) &&
        (o.option_type||null) === (pos.option_type||null);
    // TODAY's order for this contract (date filter prevents stale history matches)
    const todayOrder = clientVisibleOrders.find(o => contractMatch(o) && placedIST(o) === todayIST);
    const ordSt = (todayOrder?.status||'').toUpperCase();
    // TRADED: requires today's order AND file was generated (compliance gate)
    // Prevents: (a) stale history, (b) app-closed positions showing as "Sq-off traded"
    // ALSO requires isFlat: a contract can have an earlier, genuinely-resolved
    // round-trip today (Traded) and STILL have brand-new open exposure right
    // now (e.g. client bought again via their real trading app after that
    // round-trip closed). Without the isFlat check, the OLD resolved order
    // for this exact contract would keep showing "Traded" forever for the
    // rest of the day, hiding a real, currently-open position that still
    // needs its own square-off -- discovered 2026-07-08 on GOLDPETAL 31Aug26
    // FUT (buy 2 / sell 1 / net +1 shown as "Traded" instead of Square off).
    // FIX (2026-07-09): once flat, no order-status branch (Order received /
    // Partially traded / Order placed) should keep showing -- those all imply
    // exposure is STILL open. A position can go flat through something other
    // than THIS order being traded via RMS (e.g. the client independently
    // closed it on their own live trading app while an old ORDER_RECEIVED
    // test/duplicate order for the same contract was still sitting
    // unresolved -- confirmed 2026-07-09 on TATASTEEL, UCC 99405185: buy 1 /
    // sell 1 / net 0, yet stuck on "Order received" because that check ran
    // BEFORE the isFlat fallback below). Only "Traded" (if this order really
    // is the one RMS processed) or "No open position" apply once flat.
    if (isFlat) {
      if (todayOrder && !!todayOrder.file_generated && (ordSt==='ORDER_TRADED'||ordSt==='TRADED'))
        return <span style={{...s.placedBdg,background:T.okBg,color:T.ok}}>Traded</span>;
      return <button disabled style={s.flatDisabled}>No open position</button>;
    }
    if (todayOrder && !!todayOrder.file_generated && ordSt==='PARTIALLY_TRADED')
      return <span style={{...s.placedBdg,background:'#FFFBEB',color:'#D97706'}}>Partially traded</span>;
    // ORDER_RECEIVED / FILE_GENERATED: today's order, file_generated not required
    // (order was just placed, file not yet generated -- still valid to show status)
    if (ordSt==='ORDER_RECEIVED'||ordSt==='RECEIVED'||ordSt==='FILE_GENERATED')
      return <span style={s.placedBdg}>Order received</span>;
    if (placedFO[key] || pos.squareoff_placed) return <span style={s.placedBdg}>Order placed</span>;
    return <button style={{...s.sqBtn(true)}} className="nb-btn" onClick={()=>handleFOSquareOff(pos)}>Square off</button>;
  };
  // Sort-priority companion to foActionFor(): actionable "Square off" rows
  // sort first, in-progress order states in the middle, flat rows (Traded /
  // No open position) sort last -- so during downtime a client sees what
  // still needs action without scrolling past already-resolved positions.
  const foActionPriority = (pos) => {
    const netQty = Number(pos.net_qty);
    const isFlat = netQty === 0;
    const key = `${pos.symbol}|${pos.exchange}|${pos.segment||'FO'}|${pos.expiry_date||''}|${pos.strike_price||''}|${pos.option_type||''}`;
    const todayIST = new Date(Date.now() + 5.5*60*60*1000).toISOString().slice(0,10);
    const placedIST = o => o.placed_at
        ? new Date(new Date(o.placed_at).getTime() + 5.5*60*60*1000).toISOString().slice(0,10)
        : '';
    const contractMatch = o =>
        o.symbol   === pos.symbol &&
        o.exchange === pos.exchange &&
        (o.segment||'FO') === (pos.segment||'FO') &&
        (o.expiry_date ? o.expiry_date.slice(0,10) : null) === (pos.expiry_date ? pos.expiry_date.slice(0,10) : null) &&
        Number(o.strike_price||0) === Number(pos.strike_price||0) &&
        (o.option_type||null) === (pos.option_type||null);
    const todayOrder = clientVisibleOrders.find(o => contractMatch(o) && placedIST(o) === todayIST);
    const ordSt = (todayOrder?.status||'').toUpperCase();
    // 4 tiers, mirroring foActionFor's own branch order exactly: 0=Square off
    // (actionable), 1=in-progress order, 2=Traded, 3=No open position -- so
    // Traded and No open position no longer land in the same tier and end up
    // interleaved with each other.
    if (isFlat) {
      if (todayOrder && !!todayOrder.file_generated && (ordSt==='ORDER_TRADED'||ordSt==='TRADED')) return 2;
      return 3;
    }
    if (todayOrder && !!todayOrder.file_generated && ordSt==='PARTIALLY_TRADED') return 1;
    if (ordSt==='ORDER_RECEIVED'||ordSt==='RECEIVED'||ordSt==='FILE_GENERATED') return 1;
    if (placedFO[key] || pos.squareoff_placed) return 1;
    return 0;
  };
  const dayEquityActionFor = (pos) => {
    const dk  = pos.symbol+'_'+pos.exchange+'_'+pos.segment;
    const net = Number(pos.net_qty);
    const todayIST  = new Date(Date.now() + 5.5*60*60*1000).toISOString().slice(0,10);
    const placedIST = o => o.placed_at
        ? new Date(new Date(o.placed_at).getTime() + 5.5*60*60*1000).toISOString().slice(0,10)
        : '';
    // TODAY's order for this symbol on this exchange/segment
    const todayOrder = clientVisibleOrders.find(o =>
        o.symbol   === pos.symbol &&
        o.exchange === pos.exchange &&
        o.segment  === pos.segment &&
        placedIST(o) === todayIST
    );
    const ordSt = (todayOrder?.status||'').toUpperCase();
    // TRADED: requires today's order AND file generated (compliance gate)
    // ALSO requires net===0 -- see foActionFor's comment above for why: an
    // earlier resolved round-trip today must not mask brand-new open exposure.
    // FIX (2026-07-09): same fix as foActionFor above -- once flat, a stale
    // ORDER_RECEIVED order for the same symbol must not keep showing "Order
    // received" forever. Only "Traded" (if this order really is the one RMS
    // processed) or "No open position" apply once flat.
    if (net===0) {
      if (todayOrder && !!todayOrder.file_generated && (ordSt==='ORDER_TRADED'||ordSt==='TRADED'))
        return <span style={{...s.placedBdg,background:T.okBg,color:T.ok}}>Traded</span>;
      return <button disabled style={s.flatDisabled}>No open position</button>;
    }
    if (todayOrder && !!todayOrder.file_generated && ordSt==='PARTIALLY_TRADED')
      return <span style={{...s.placedBdg,background:'#FFFBEB',color:'#D97706'}}>Partially traded</span>;
    // ORDER_RECEIVED / FILE_GENERATED: today's order, file not required yet
    if (ordSt==='ORDER_RECEIVED'||ordSt==='RECEIVED'||ordSt==='FILE_GENERATED')
      return <span style={s.placedBdg}>Order received</span>;
    if (placedDayEquity[dk]) return <span style={s.placedBdg}>Order placed</span>;
    return <button style={{...s.sqBtn(true)}} className="nb-btn" onClick={()=>handleDayEquitySquareOff(pos)}>Square off</button>;
  };
  // Sort-priority companion to dayEquityActionFor() -- same idea as
  // foActionPriority above.
  const dayEquityActionPriority = (pos) => {
    const dk  = pos.symbol+'_'+pos.exchange+'_'+pos.segment;
    const net = Number(pos.net_qty);
    const todayIST  = new Date(Date.now() + 5.5*60*60*1000).toISOString().slice(0,10);
    const placedIST = o => o.placed_at
        ? new Date(new Date(o.placed_at).getTime() + 5.5*60*60*1000).toISOString().slice(0,10)
        : '';
    const todayOrder = clientVisibleOrders.find(o =>
        o.symbol   === pos.symbol &&
        o.exchange === pos.exchange &&
        o.segment  === pos.segment &&
        placedIST(o) === todayIST
    );
    const ordSt = (todayOrder?.status||'').toUpperCase();
    // Same 4-tier scheme as foActionPriority above.
    if (net === 0) {
      if (todayOrder && !!todayOrder.file_generated && (ordSt==='ORDER_TRADED'||ordSt==='TRADED')) return 2;
      return 3;
    }
    if (todayOrder && !!todayOrder.file_generated && ordSt==='PARTIALLY_TRADED') return 1;
    if (ordSt==='ORDER_RECEIVED'||ordSt==='RECEIVED'||ordSt==='FILE_GENERATED') return 1;
    if (placedDayEquity[dk]) return 1;
    return 0;
  };
  const holdingsActionFor = (h) => {
    if (!isEquityHolding(h.company_name, h.isin)) {
      return <span style={s.placedBdg}>View only</span>;
    }
    // FIX (2026-07-20): Holdings now gets the SAME Traded/Partially traded/
    // Order received pill scheme as Day/Net (orderStatusLabel above), driven
    // by the backend's today_status field (per-ISIN, resolved the same way
    // the Holdings quantity netting is -- ORDER_TRADED/PARTIALLY_TRADED
    // require file_generated, matching the compliance gate dayEquityActionFor
    // and foActionFor already use). Previously this only ever showed "Order
    // received" from the client-local placedHoldings flag (reset on reload)
    // or a bare "Square off" button -- so once RMS actually executed the
    // trade, the row's number quietly changed but nothing told the user it
    // had traded, unlike every other tab in the app.
    if (h.today_status === 'TRADED')
      return <span style={{...s.placedBdg,background:T.okBg,color:T.ok}}>Traded</span>;
    if (h.today_status === 'PARTIALLY_TRADED')
      return <span style={{...s.placedBdg,background:'#FFFBEB',color:'#D97706'}}>Partially traded</span>;
    // Label AND color both match the terminology/color used everywhere
    // else in the app (Net/Day/Orders tabs -> orderStatusLabel) for this
    // exact same ORDER_RECEIVED/FILE_GENERATED state: blue (T.brand /
    // T.brandL), not s.placedBdg's default amber, which elsewhere in this
    // file is actually the "Partially traded" color, not "Order received".
    return h.today_status === 'ORDER_RECEIVED' || placedHoldings[(h.isin||'').toUpperCase()] || placedHoldings[h.isin]
      ? <span style={{...s.placedBdg,background:T.brandL,color:T.brand}}>Order received</span>
      : <button style={{...s.sqBtn(true)}} className="nb-btn" onClick={()=>handleHoldingSquareOff(h)}>Square off</button>;
  };
  const orderStatusLabel = (raw, isStale) => {
    const st = (raw||'').toUpperCase();
    // Stale: order still sits at Order received in squareoff_orders, but the
    // position it targeted has already gone flat -- e.g. the client squared
    // off the same symbol from their own live trading app instead of via
    // Navia Backup. Backend (orders.js /my-orders) now computes is_stale the
    // same way for CM (day_positions) and FO (positions) as the admin/dealer
    // views already do. Showing "No open position" here (instead of a stale
    // "Order received") makes My Orders agree with what Day/Net already show
    // for this symbol, instead of looking like two different truths.
    if (isStale && (st==='ORDER_RECEIVED'||st==='RECEIVED'||st==='FILE_GENERATED'))
      return {label:'No open position', col:T.tx3, bg:T.surf2};
    if (st==='ORDER_RECEIVED'||st==='RECEIVED'||st==='FILE_GENERATED') return {label:'Order received', col:'#1B4FD8', bg:'#EEF2FF'};
    if (st==='PARTIALLY_TRADED')                                      return {label:'Partially traded', col:'#D97706', bg:'#FFFBEB'};
    if (st==='TRADED'||st==='ORDER_TRADED')                            return {label:'Traded', col:T.ok, bg:T.okBg};
    if (st==='REJECTED')                                               return {label:'Rejected', col:T.err, bg:T.errBg};
    return {label:'Order received', col:'#1B4FD8', bg:'#EEF2FF'};
  };


  // Client-visible orders only (exclude internal FILE_GENERATED status)
  // FIX (10-Jul-2026): this whitelist was missing PARTIALLY_TRADED -- so a
  // client order that reconciliationService correctly moved to that status
  // (e.g. GOLDPETAL, 2 lots requested, 1 executed so far) was silently
  // excluded from the My Orders tab entirely: not counted in the tab badge,
  // not shown in the table, and also invisible to every contractMatch/
  // todayOrder lookup on the Net/Day/B-F tabs (they all read from
  // clientVisibleOrders too) -- so this same gap could also affect the
  // action-button decision for that symbol elsewhere on the dashboard, not
  // just the Orders list. The dealer Orders tab was never filtered this way
  // (DealerDashboard.jsx renders every order returned by the API, no status
  // whitelist), which is why GOLDPETAL showed there but not here.
  const clientVisibleOrders = orders.filter(o => {
    const st = (o.status||'').toUpperCase();
    return st==='ORDER_RECEIVED'||st==='RECEIVED'||st==='FILE_GENERATED'||st==='PARTIALLY_TRADED'||st==='TRADED'||st==='ORDER_TRADED'||st==='REJECTED'||st==='FAILED';
  });
  const tabs = [
    {key:'positions',label:'Net',     full:'Net position',  count:netCount},
    {key:'day',      label:'Day',     full:'Day position',  count:dayPositions.length},
    {key:'bf',       label:'B/F',     full:'B/F position',  count:bfCount},
    {key:'holdings', label:'Holdings',full:'Holdings',      count:holdingsCount},
    {key:'orders',   label:'Orders',  full:'My orders',     count:clientVisibleOrders.length},
  ];

  /* ──────────────────────────────────────────────────────────────────────────
     RENDER
  ────────────────────────────────────────────────────────────────────────── */
  return (
    <div style={s.page}>

      {/* ── Confirm modal ── */}
      {confirmModal && (
        <div style={s.overlay} role="dialog" aria-modal="true">
          <div style={s.modal} className="nb-slide-up">
            <div style={{padding:'18px 20px 14px',borderBottom:`0.5px solid ${T.bdr}`}}>
              <div style={{display:'flex',alignItems:'center',gap:12}}>
                <div style={{width:36,height:36,background:T.errBg,border:`0.5px solid ${T.err}`,borderRadius:9,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={T.err} strokeWidth="2"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                </div>
                <div>
                  <div style={{fontSize:fs+1,fontWeight:600,color:T.tx}}>Confirm square-off</div>
                  <div style={{fontSize:11,color:T.tx2,marginTop:2}}>This action cannot be undone</div>
                </div>
              </div>
            </div>
            <div style={{padding:'16px 20px'}}>
              <div style={{background:T.surf2,border:`0.5px solid ${T.bdr}`,borderRadius:8,overflow:'hidden',marginBottom:13}}>
                {[
                  {label:'Symbol',    value:confirmModal.displayName||confirmModal.symbol},
                  {label:'Exchange',  value:`${confirmModal.exchange} / ${confirmModal.segment}`},
                  {label:'Side',      value:confirmModal.side, col:T.err},
                  {label:'Quantity',  value:confirmModal.quantity},
                  {label:'Order type',value:'Market'},
                ].map((r,i)=>(
                  <div key={i} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'9px 13px',borderBottom:i<4?`0.5px solid ${T.bdr}`:'none'}}>
                    <span style={{fontSize:12,color:T.tx2}}>{r.label}</span>
                    <span style={{fontSize:12,fontWeight:500,color:r.col||T.tx}}>{r.value}</span>
                  </div>
                ))}
              </div>
              <div style={{background:T.errBg,border:`0.5px solid ${T.err}`,borderRadius:8,color:T.errTx,fontSize:12,padding:'9px 12px',lineHeight:1.6}}>
                Order executes at market price and cannot be cancelled after placing.
              </div>
            </div>
            <div style={{padding:'12px 20px 18px',borderTop:`0.5px solid ${T.bdr}`,display:'flex',gap:10}}>
              <button className="nb-btn" style={{flex:1,height:40,background:'transparent',border:`0.5px solid ${T.bdr2}`,borderRadius:8,color:T.tx,fontSize:13,cursor:'pointer',fontFamily:"'DM Sans',sans-serif"}} onClick={()=>setConfirmModal(null)} disabled={placing}>Cancel</button>
              <button className="nb-btn" style={{flex:2,height:40,background:T.err,border:'none',borderRadius:8,color:'#fff',fontSize:13,fontWeight:600,cursor:'pointer',fontFamily:"'DM Sans',sans-serif"}} onClick={confirmSquareOff} disabled={placing}>{placing?'Placing…':'Confirm square-off'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Square Off All modal ── */}
      {sqOffAllModal && (
        <div style={s.overlay} role="dialog" aria-modal="true">
          <div style={{...s.modal, maxWidth: 480}} className="nb-slide-up">
            <div style={{padding:"18px 20px 14px", borderBottom:`0.5px solid ${T.bdr}`}}>
              <div style={{display:"flex", alignItems:"center", gap:12}}>
                <div style={{width:36,height:36,background:T.errBg,border:`0.5px solid ${T.err}`,borderRadius:9,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={T.err} strokeWidth="2"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                </div>
                <div>
                  <div style={{fontSize:fs+1, fontWeight:600, color:T.tx}}>
                    {"Square off all - " + sqOffAllModal.positions.length + " position" + (sqOffAllModal.positions.length>1?"s":"")}
                  </div>
                  <div style={{fontSize:11, color:T.tx2, marginTop:2}}>All orders execute at market price</div>
                </div>
              </div>
            </div>
            <div style={{padding:"14px 20px", maxHeight:320, overflowY:"auto"}}>
              <div style={{background:T.surf2, border:`0.5px solid ${T.bdr}`, borderRadius:8, overflow:"hidden", marginBottom:12}}>
                <div style={{display:"grid", gridTemplateColumns:"1fr 80px 60px 80px", padding:"7px 12px", background:T.surf, borderBottom:`0.5px solid ${T.bdr}`}}>
                  {["Symbol","Side","Qty","Exchange"].map(h => (
                    <div key={h} style={{fontSize:10, fontWeight:600, color:T.tx3, textTransform:"uppercase", letterSpacing:".04em"}}>{h}</div>
                  ))}
                </div>
                {sqOffAllModal.positions.map((pos, i) => {
                  const side = Number(pos.net_qty) > 0 ? "SELL" : "BUY";
                  const qty  = Math.abs(Number(pos.net_qty));
                  return (
                    <div key={i} style={{display:"grid", gridTemplateColumns:"1fr 80px 60px 80px", padding:"8px 12px", borderBottom: i < sqOffAllModal.positions.length-1 ? `0.5px solid ${T.bdr}` : "none", alignItems:"center"}}>
                      <div style={{fontSize:12, fontWeight:600, color:T.tx}}>
                        {pos.symbol}
                        {pos.strike_price && <span style={{fontSize:10, color:T.tx3, marginLeft:4}}>{Math.round(pos.strike_price)} {pos.option_type||""}</span>}
                        {pos.expiry_date  && <span style={{fontSize:10, color:T.tx3, marginLeft:4}}>{"· " + new Date(pos.expiry_date).toLocaleDateString("en-IN",{day:"2-digit",month:"short"})}</span>}
                      </div>
                      <div style={{fontSize:12, fontWeight:600, color:side==="SELL"?T.sell:T.buy}}>{side}</div>
                      <div style={{fontSize:12, fontWeight:500, color:T.tx}}>{qty}</div>
                      <div><ExchBadge exchange={pos.exchange} segment={pos.segment||"FO"}/></div>
                    </div>
                  );
                })}
              </div>
              <div style={{background:T.errBg, border:`0.5px solid ${T.err}`, borderRadius:8, color:T.errTx, fontSize:12, padding:"9px 12px", lineHeight:1.6}}>
                {"This will place " + sqOffAllModal.positions.length + " market order" + (sqOffAllModal.positions.length>1?"s":"") + ". RMS team will be notified for each. This action cannot be undone."}
              </div>
            </div>
            <div style={{padding:"12px 20px 18px", borderTop:`0.5px solid ${T.bdr}`, display:"flex", gap:10}}>
              <button className="nb-btn" style={{flex:1,height:40,background:"transparent",border:`0.5px solid ${T.bdr2}`,borderRadius:8,color:T.tx,fontSize:13,cursor:"pointer",fontFamily:"DM Sans,sans-serif"}} onClick={()=>setSqOffAllModal(null)} disabled={sqOffAllPlacing}>Cancel</button>
              <button className="nb-btn" style={{flex:2,height:40,background:T.err,border:"none",borderRadius:8,color:"#fff",fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:"DM Sans,sans-serif"}} onClick={confirmSqOffAll} disabled={sqOffAllPlacing}>
                {sqOffAllPlacing ? "Placing orders..." : "Confirm - square off all " + sqOffAllModal.positions.length}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── SSO block modal ── */}
      {ssoBlockModal && (
        <div style={s.overlay} role="alertdialog" aria-modal="true">
          <div style={{...s.modal,maxWidth:400}} className="nb-slide-up">
            <div style={{padding:'18px 20px 14px',borderBottom:`0.5px solid ${T.bdr}`}}>
              <div style={{fontSize:15,fontWeight:600,color:T.tx}}>Square-off unavailable</div>
              <div style={{fontSize:12,color:T.tx2,marginTop:2}}>Trading platform is functioning</div>
            </div>
            <div style={{padding:'16px 20px'}}>
              <div style={{background:T.warnBg,border:`0.5px solid ${T.warn}`,borderRadius:8,padding:16,color:T.warnTx,fontSize:fs-1,lineHeight:1.7}}>
                It seems our trading application is functioning. Square-off via Navia Backup is only available during platform downtime.
              </div>
            </div>
            <div style={{padding:'12px 20px 18px',borderTop:`0.5px solid ${T.bdr}`}}>
              <button className="nb-btn" style={{width:'100%',height:40,background:T.brand,border:'none',borderRadius:8,color:'#fff',fontSize:13,fontWeight:600,cursor:'pointer',fontFamily:"'DM Sans',sans-serif"}} onClick={()=>setSsoBlockModal(false)}>OK, understood</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Profile drawer ── */}
      {showProfile && (
        <>
          <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.5)',zIndex:200}} onClick={()=>setShowProfile(false)}/>
          <div style={s.drawer} className="nb-slide-up" role="dialog" aria-modal="true" aria-label="Account profile">
            <div style={{width:36,height:4,background:T.bdr2,borderRadius:99,margin:'12px auto'}}/>
            <div style={{padding:'4px 20px 14px',borderBottom:`0.5px solid ${T.bdr}`,display:'flex',alignItems:'center',justifyContent:'space-between'}}>
              <div style={{fontSize:15,fontWeight:600,color:T.tx}}>My account</div>
              <button style={{...s.iconBtn,background:'transparent'}} onClick={()=>setShowProfile(false)}>✕</button>
            </div>
            <div style={{padding:'18px 20px',borderBottom:`0.5px solid ${T.bdr}`,display:'flex',alignItems:'center',gap:13}}>
              <div style={{width:50,height:50,background:T.brandL,borderRadius:'50%',display:'flex',alignItems:'center',justifyContent:'center',fontSize:17,fontWeight:600,color:T.brand,flexShrink:0}}>{getInitials(client.name)}</div>
              <div>
                <div style={{fontSize:15,fontWeight:600,color:T.tx}}>{client.name}</div>
                <div style={{fontSize:12,color:T.tx2,marginTop:2}}>UCC: {client.ucc}</div>
                <div style={{fontSize:10,fontWeight:500,background:T.okBg,color:T.okTx,borderRadius:99,padding:'2px 9px',display:'inline-block',marginTop:5}}>Account active</div>
              </div>
            </div>
            {[
              {icon:'🪪', label:'Client code', value:(profileData||client).ucc||'—'},
              {icon:'📅', label:'Date of birth', value:(profileData||client).dob ? new Date((profileData||client).dob).toLocaleDateString('en-IN',{day:'2-digit',month:'2-digit',year:'numeric'}) : '—'},
              {icon:'📱', label:'Mobile', value:(profileData?.mobile||client.mobile) ? `+91 ••••••${String(profileData?.mobile||client.mobile).slice(-4)}` : '—'},
              {icon:'✉',  label:'Email', value:(profileData?.email||client.email) ? `${(profileData?.email||client.email).charAt(0)}${'*'.repeat(5)}@${(profileData?.email||client.email).split('@')[1]||'—'}` : '—'},
            ].map((r,i)=>(
              <div key={i} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'12px 20px',borderBottom:`0.5px solid ${T.bdr}`,fontSize:13}}>
                <span style={{color:T.tx2,display:'flex',alignItems:'center',gap:8}}><span>{r.icon}</span>{r.label}</span>
                <span style={{fontWeight:500,color:T.tx}}>{r.value}</span>
              </div>
            ))}
            <div style={{padding:'14px 20px',borderBottom:`0.5px solid ${T.bdr}`,background:T.surf2}}>
              <div style={{fontSize:11,fontWeight:600,color:T.tx2,textTransform:'uppercase',letterSpacing:'.06em',marginBottom:10}}>Need help?</div>
              <a href="mailto:support@navia.co.in" style={{display:'flex',alignItems:'center',gap:8,fontSize:13,color:T.brand,textDecoration:'none',marginBottom:8}}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                support@navia.co.in
              </a>
              <a href="https://support.navia.co.in" target="_blank" rel="noreferrer" style={{display:'flex',alignItems:'center',gap:8,fontSize:13,color:T.brand,textDecoration:'none'}}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/></svg>
                support.navia.co.in
              </a>
            </div>
            <div style={{padding:'12px 20px 22px'}}>
              <button className="nb-btn" style={{width:'100%',background:T.errBg,color:T.err,border:`0.5px solid ${T.err}`,borderRadius:8,padding:12,fontSize:13,fontWeight:500,cursor:'pointer',fontFamily:"'DM Sans',sans-serif",display:'flex',alignItems:'center',justifyContent:'center',gap:8}} onClick={onLogout}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
                Sign out
              </button>
            </div>
          </div>
        </>
      )}

      {/* ── Help popup ── */}
      {showHelp && (
        <div style={{...s.overlay,zIndex:2000}} role="dialog" aria-modal="true" onClick={e=>e.target===e.currentTarget&&setShowHelp(false)}>
          <div style={{...s.modal,maxWidth:380}} className="nb-slide-up">
            <div style={{padding:'18px 20px 14px',borderBottom:`0.5px solid ${T.bdr}`,display:'flex',alignItems:'center',justifyContent:'space-between'}}>
              <div style={{fontSize:15,fontWeight:600,color:T.tx}}>Need help?</div>
              <button style={{...s.iconBtn,background:'transparent'}} onClick={()=>setShowHelp(false)}>✕</button>
            </div>
            <div style={{padding:'18px 20px',display:'flex',flexDirection:'column',gap:12}}>
              {[
                {icon:<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={T.brand} strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/></svg>, label:'Support portal', sub:'support.navia.co.in', href:'https://support.navia.co.in'},
                {icon:<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={T.brand} strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>, label:'Email support', sub:'support@navia.co.in', href:'mailto:support@navia.co.in'},
              ].map((item,i)=>(
                <a key={i} href={item.href} target="_blank" rel="noreferrer"
                  style={{display:'flex',alignItems:'center',gap:14,padding:'13px 15px',background:T.surf2,border:`0.5px solid ${T.bdr}`,borderRadius:10,textDecoration:'none',transition:'.15s'}}>
                  <div style={{width:38,height:38,background:T.brandL,borderRadius:9,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>{item.icon}</div>
                  <div>
                    <div style={{fontSize:13,fontWeight:500,color:T.tx,marginBottom:2}}>{item.label}</div>
                    <div style={{fontSize:12,color:T.brand}}>{item.sub}</div>
                  </div>
                </a>
              ))}
            </div>
            <div style={{padding:'0 20px 18px'}}>
              <button className="nb-btn" style={{width:'100%',height:40,background:'transparent',border:`0.5px solid ${T.bdr2}`,borderRadius:8,color:T.tx,fontSize:13,cursor:'pointer',fontFamily:"'DM Sans',sans-serif"}} onClick={()=>setShowHelp(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Accessibility drawer ── */}
      {showA11y && (
        <>
          <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.5)',zIndex:200}} onClick={()=>setShowA11y(false)}/>
          <div style={s.drawer} className="nb-slide-up" role="dialog" aria-modal="true" aria-label="Accessibility settings">
            <div style={{width:36,height:4,background:T.bdr2,borderRadius:99,margin:'12px auto'}}/>
            <div style={{padding:'4px 20px 14px',borderBottom:`0.5px solid ${T.bdr}`,display:'flex',alignItems:'center',justifyContent:'space-between'}}>
              <div style={{fontSize:15,fontWeight:600,color:T.tx}}>Accessibility settings</div>
              <button style={{...s.iconBtn,background:'transparent'}} onClick={()=>setShowA11y(false)}>✕</button>
            </div>
            {/* Font size */}
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'14px 20px',borderBottom:`0.5px solid ${T.bdr}`}}>
              <div><div style={{fontSize:14,fontWeight:500,color:T.tx}}>Text size</div><div style={{fontSize:11,color:T.tx2,marginTop:2}}>12 – 20px range</div></div>
              <div style={{display:'flex',alignItems:'center',gap:10}}>
                <button style={{width:32,height:32,border:`0.5px solid ${T.bdr2}`,borderRadius:'50%',background:T.surf2,cursor:'pointer',fontSize:17,display:'flex',alignItems:'center',justifyContent:'center',color:T.tx}} onClick={()=>setFontSize(f=>Math.max(12,f-1))}>−</button>
                <span style={{fontSize:13,fontWeight:500,color:T.tx,minWidth:34,textAlign:'center'}} aria-live="polite">{fontSize}px</span>
                <button style={{width:32,height:32,border:`0.5px solid ${T.bdr2}`,borderRadius:'50%',background:T.surf2,cursor:'pointer',fontSize:17,display:'flex',alignItems:'center',justifyContent:'center',color:T.tx}} onClick={()=>setFontSize(f=>Math.min(20,f+1))}>+</button>
              </div>
            </div>
            {/* Theme */}
            <div style={{padding:'14px 20px',borderBottom:`0.5px solid ${T.bdr}`}}>
              <div style={{fontSize:13,fontWeight:500,color:T.tx,marginBottom:10}}>Colour theme</div>
              <div style={{display:'flex',gap:8}}>
                {[{k:'light',l:'☀ Light',bg:'#fff',col:'#0D0F12'},{k:'dark',l:'🌙 Dark',bg:'#141928',col:'#F0F4FF'},{k:'hc',l:'⬤ High contrast',bg:'#000',col:'#fff'}].map(t=>(
                  <button key={t.k} onClick={()=>setThemeKey(t.k)} aria-pressed={themeKey===t.k}
                    style={{flex:1,padding:'9px 5px',borderRadius:8,fontSize:11,fontWeight:500,cursor:'pointer',fontFamily:"'DM Sans',sans-serif",border:`1.5px solid ${themeKey===t.k?T.brand:'transparent'}`,background:t.bg,color:t.col}}>{t.l}</button>
                ))}
              </div>
            </div>
            {[
              {label:'Reduce motion',sub:'Minimise animations',val:reduceMotion,set:setReduceMotion},
              {label:'Larger tap targets',sub:'Bigger buttons for touch',val:largeTT,set:setLargeTT},
              {label:'Focus indicators',sub:'Always show keyboard focus ring',val:focusRing,set:setFocusRing},
            ].map((row,i)=>(
              <div key={i} style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'13px 20px',borderBottom:`0.5px solid ${T.bdr}`}}>
                <div><div style={{fontSize:13,fontWeight:500,color:T.tx}}>{row.label}</div><div style={{fontSize:11,color:T.tx2,marginTop:2}}>{row.sub}</div></div>
                <Toggle on={row.val} onToggle={()=>row.set(v=>!v)} label={row.label}/>
              </div>
            ))}
            <div style={{padding:'13px 20px 22px',background:T.surf2,borderRadius:'0 0 20px 20px',fontSize:11,color:T.tx2,lineHeight:1.7}}>
              ℹ Navia Backup targets WCAG 2.1 AA compliance. All square-off actions require explicit confirmation.
            </div>
          </div>
        </>
      )}

      {/* ── Top bar ── */}
      <header style={s.topBar}>
        <div style={{display:'flex',alignItems:'center',gap:10}}>
          <div style={{
            background:'#fff',borderRadius:8,padding:'4px 10px',
            display:'flex',alignItems:'center',justifyContent:'center',
            flexShrink:0,boxShadow:'0 1px 4px rgba(0,0,0,.12)',
            height:34,boxSizing:'border-box',overflow:'hidden'
          }}>
            <img src={naviaLogo}
              style={{height:26,width:'auto',objectFit:'contain',display:'block'}}
              alt="Navia Markets"
            />
          </div>
          <div>
            <div style={s.brandName}>Navia Backup</div>
            <div style={s.brandSub}>UCC: {client.ucc}</div>
          </div>
        </div>
        <div style={{display:'flex',alignItems:'center',gap:8}}>
          <button style={s.iconBtn} className="nb-btn" onClick={()=>setShowHelp(true)} aria-label="Need help?">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
          </button>
          <button style={s.iconBtn} className="nb-btn" onClick={()=>setShowA11y(true)} aria-label="Accessibility settings">♿</button>
          <button style={s.avatarBtn} className="nb-btn" onClick={()=>setShowProfile(true)} aria-label={`Profile: ${client.name}`}>{getInitials(client.name)}</button>
        </div>
      </header>

      {/* ── Alert strip ── */}
      <PWDBanner/>

      {/* ── Message banner ── */}
      {message && (
        <div style={{...s.msgBanner,background:messageType==='success'?T.okBg:T.errBg,border:`0.5px solid ${messageType==='success'?T.ok:T.err}`,color:messageType==='success'?T.okTx:T.errTx}} role="status" className="nb-fade">
          {message}
        </div>
      )}

      {/* ── Main ── */}
      <main style={s.body}>
        {/* Stat cards */}
        <div style={s.statGrid}>
          {[
            {key:'positions',label:'Net positions', count:netCount,    tagBg:T.brandL,tagCol:T.brandD},
            {key:'day',      label:'Day trades',    count:dayLoading?'…':dayPositions.length, tagBg:'#E8F5E9',tagCol:'#27500A'},
            {key:'bf',       label:'Carried fwd',   count:bfLoading?'…':bfCount,  tagBg:'#FFF8E1',tagCol:'#633806'},
            {key:'holdings', label:'Holdings',      count:holdingsLoading?'…':holdingsCount, tagBg:'#EEEDFE',tagCol:'#3C3489'},
          ].map(card=>(
            <div key={card.key} style={s.statCard(activeTab===card.key)} className="nb-card-hov nb-btn"
              onClick={()=>setActiveTab(card.key)} tabIndex={0} role="button" aria-pressed={activeTab===card.key}
              onKeyDown={e=>e.key==='Enter'&&setActiveTab(card.key)}>
              <div style={s.statTag(card.tagBg,card.tagCol)}>{card.key==='positions'?'NET':card.key==='day'?'DAY':card.key==='bf'?'B/F':'HOL'}</div>
              <div style={s.statNum(activeTab===card.key)}>{card.count}</div>
              <div style={s.statLbl}>{card.label}</div>
            </div>
          ))}
          <div style={{...s.statCard(false),background:T.okBg,borderColor:T.ok,cursor:'default',...(isMobile?{gridColumn:'1 / -1'}:{})}}>
            <div style={s.statTag(T.okBg,T.okTx)}>LIVE</div>
            <div style={{fontSize:20,fontWeight:500,color:T.ok}}>Active</div>
            <div style={{...s.statLbl,color:T.okTx}}>Emergency mode on</div>
          </div>
        </div>

        {/* Tab bar */}
        <nav style={s.tabRow}>
          {tabs.map((t,i)=>(
            <button key={t.key} style={s.tabPill(activeTab===t.key,i===tabs.length-1)}
              onClick={()=>setActiveTab(t.key)} aria-current={activeTab===t.key?'page':undefined}>
              {isMobile?t.label:t.full}
              <span style={{marginLeft:4,fontSize:9,opacity:.7}}>({t.count})</span>
            </button>
          ))}
        </nav>

        {/* ── REFRESH BAR + NET SEARCH ── */}
        <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', padding:'8px 0 2px', gap:10}}>
          {activeTab==='positions' && netFOPositions.length>0
            ? <input
                type="text"
                value={netSearch}
                onChange={e => setNetSearch(e.target.value)}
                placeholder="Search symbol…"
                style={{flex:'0 1 220px',padding:'7px 12px',borderRadius:8,border:`1px solid ${T.bdr}`,background:T.surf,color:T.tx,fontSize:12,fontFamily:"'DM Sans',sans-serif",outline:'none'}}
              />
            : <div/>
          }
          <button
            onClick={async ()=>{
              // Set loading for ALL tabs so spinner shows regardless of active tab
              setLoading(true);
              setBfLoading(true);
              setDayLoading(true);
              setHoldingsLoading(true);
              await Promise.all([loadData(), loadBF(), loadHoldings(), loadDay()]);
              setLoading(false);
              setBfLoading(false);
              setDayLoading(false);
              setHoldingsLoading(false);
            }}
            style={{display:'flex',alignItems:'center',gap:6,background:T.brandL,border:`1px solid ${T.brand}`,borderRadius:8,padding:'7px 18px',fontSize:12,fontWeight:600,color:T.brand,cursor:'pointer',fontFamily:"'DM Sans',sans-serif",flexShrink:0}}>
            ↻ Refresh data
          </button>
        </div>

        {/* ── NET POSITION TAB — FAO ONLY (per spec: EQ sq-off is via Day positions tab) ──
         * Single unified table only. The previous design showed TWO separate tables
         * ("Open positions/Live" sourced from today's day_positions, and "F&O net
         * positions/B/F+Day combined") which mathematically duplicate each other
         * whenever a contract has no B/F carry-forward (the common case for a
         * fresh intraday trade) — producing confusing, SEBI-non-compliant duplicate
         * rows for the same contract. netFOPositions already correctly covers every
         * case (today-only, B/F-only, or both combined) in one net row per contract. */}
        {activeTab==='positions' && (
          <div className="nb-fade">
            {loading
              ? <div style={s.emptyState}>Loading positions…</div>
              : <>
                  {(() => {
                    const filtered = (netSearch.trim()
                      ? netFOPositions.filter(p => {
                          const label = formatStrikeLabel(p.symbol, p.strike_price).toUpperCase();
                          const sym   = (p.symbol||'').toUpperCase();
                          const q     = netSearch.trim().toUpperCase();
                          return sym.includes(q) || label.includes(q);
                        })
                      : netFOPositions
                    ).slice().sort((a,b)=>foActionPriority(a)-foActionPriority(b));
                    return (
                      <>
                        {filtered.length>0 && (
                          <>
                            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:6}}>
                              <SectionHdr title="F&O net positions" badge="Today" count={filtered.length}/>
                              {filtered.filter(p=>Number(p.net_qty)!==0).length>0 && (
                                <button className="nb-btn" onClick={()=>handleSqOffAll(filtered,'fo')}
                                  style={{background:T.err,border:'none',borderRadius:7,padding:'6px 14px',fontSize:11,fontWeight:600,color:'#fff',cursor:'pointer',fontFamily:"'DM Sans',sans-serif",flexShrink:0}}>
                                  Square off all F&O
                                </button>
                              )}
                            </div>
                            {isMobile ? (
                              <div>
                                {filtered.map((pos,i)=>(
                                  <MobileFOCard key={`mnet_${i}`}
                                    symbol={pos.symbol} strikePrice={pos.strike_price} expiryDate={pos.expiry_date}
                                    optionType={pos.option_type||'FUT'} exchange={pos.exchange} segment={pos.segment}
                                    buyQty={pos.total_buy} sellQty={pos.total_sell} netQty={pos.net_qty}
                                    action={foActionFor(pos)}/>
                                ))}
                              </div>
                            ) : (
                            <div style={s.tcard}>
                              <div style={{overflowX:'auto'}}>
                                <table style={{width:'100%',borderCollapse:'collapse',minWidth:620}}>
                                  <thead>
                                    <tr>
                                      {['Symbol & strike','Expiry','Type','Buy qty','Sell qty','Net qty','Exchange','Action'].map(h=>(
                                        <th key={h} style={s.th}>{h}</th>
                                      ))}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {filtered.map((pos,i)=>{
                                      const netQty=Number(pos.net_qty);
                                      return (
                                        <tr key={`net_${i}`} style={{borderBottom:`0.5px solid ${T.bdr}`}}>
                                          <td style={s.td}>
                                            <div style={{fontWeight:600,color:T.tx}}>{pos.symbol}</div>
                                            {pos.strike_price&&<div style={{fontSize:11,color:T.tx2}}>{formatStrikeLabel(pos.symbol,pos.strike_price)}</div>}
                                          </td>
                                          <td style={{...s.td,fontSize:12,color:T.tx2}}>{formatExpiry(pos.expiry_date)}</td>
                                          <td style={s.td}><span style={s.typePill(pos.option_type)}>{pos.option_type||'FUT'}</span></td>
                                          <td style={{...s.td,color:T.buy,fontWeight:500}}>{fmtQty(pos.total_buy)}</td>
                                          <td style={{...s.td,color:T.sell,fontWeight:500}}>{fmtQty(pos.total_sell)}</td>
                                          <td style={s.td}><QtyDisplay v={netQty}/></td>
                                          <td style={s.td}><ExchBadge exchange={pos.exchange} segment={pos.segment}/></td>
                                          <td style={s.td}>{foActionFor(pos)}</td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                            )}
                          </>
                        )}
                        {filtered.length===0 && (
                          <div style={s.emptyState}>
                            {netSearch.trim() ? `No positions matching "${netSearch}"` : 'No net position for the day.'}
                          </div>
                        )}
                      </>
                    );
                  })()}
                </>
            }
          </div>
        )}

        {/* ── DAY POSITION TAB ── */}
        {activeTab==='day' && (
          <div className="nb-fade">
            {dayLoading ? <div style={s.emptyState}>Loading day positions…</div>
            : dayPositions.length===0 ? <div style={s.emptyState}>No day positions found for today.</div>
            : <>
                <div style={s.sumBar}>
                  {[{l:'Total net',v:fmtQty(dayTotalNet),c:T.brand},{l:'Equity',v:dayEquity.length,c:T.buy},{l:'Options',v:dayOptions.length,c:'#7c3aed'},{l:'Futures',v:dayFutures.length,c:T.warn}].map((item,i)=>(
                    <div key={i} style={s.sumItem}><div style={s.sumLbl}>{item.l}</div><div style={s.sumVal(item.c)}>{item.v}</div></div>
                  ))}
                  <div style={{...s.sumItem,marginLeft:'auto',display:'flex',alignItems:'center',gap:10}}>
                    <div style={s.vonly}>Equity sq-off · F&O view only</div>
                    {dayEquity.filter(p=>Number(p.net_qty)!==0).length>0 && (
                      <button className="nb-btn" onClick={()=>handleSqOffAll(dayEquity,'cm')}
                        style={{background:T.err,border:'none',borderRadius:7,padding:'6px 14px',fontSize:11,fontWeight:600,color:'#fff',cursor:'pointer',fontFamily:"'DM Sans',sans-serif",flexShrink:0}}>
                        Square off all CM
                      </button>
                    )}
                  </div>
                </div>
                {/* Search row — left aligned, same layout as Net position tab */}
                {dayEquity.length>0 && (
                  <div style={{display:'flex',alignItems:'center',justifyContent:'flex-start',padding:'8px 0 2px'}}>
                    <input type="text" value={daySearch} onChange={e=>setDaySearch(e.target.value)}
                      placeholder="Search symbol…"
                      style={{flex:'0 1 220px',padding:'7px 12px',borderRadius:8,border:`1px solid ${T.bdr}`,background:T.surf,color:T.tx,fontSize:12,fontFamily:"'DM Sans',sans-serif",outline:'none'}}/>
                  </div>
                )}
                {(() => {
                  const filteredEquity = (daySearch.trim()
                    ? dayEquity.filter(p => (p.symbol||'').toUpperCase().includes(daySearch.trim().toUpperCase()))
                    : dayEquity
                  ).slice().sort((a,b)=>dayEquityActionPriority(a)-dayEquityActionPriority(b));
                  return (<>
                {filteredEquity.length>0&&(<>
                  <SectionHdr title="Equity" badge="Cash market" count={filteredEquity.length}/>
                  {isMobile ? (
                    <div>
                      {filteredEquity.map((pos,i)=>(
                        <MobileFOCard key={`meqd_${i}`}
                          symbol={pos.symbol} optionType="EQ" exchange={pos.exchange} segment={pos.segment}
                          buyQty={pos.buy_qty} sellQty={pos.sell_qty} netQty={pos.net_qty}
                          action={dayEquityActionFor(pos)}/>
                      ))}
                    </div>
                  ) : (
                  <div style={s.tcard}>
                    <div style={{overflowX:'auto'}}>
                      <table style={{width:'100%',borderCollapse:'collapse',minWidth:480}}>
                        <thead><tr>{['Symbol','Buy qty','Sell qty','Net qty','Exchange','Action'].map(h=><th key={h} style={s.th}>{h}</th>)}</tr></thead>
                        <tbody>
                          {filteredEquity.map((pos,i)=>(
                            <tr key={i} style={{borderBottom:`0.5px solid ${T.bdr}`}}>
                              <td style={{...s.td,fontWeight:600}}>{pos.symbol}</td>
                              <td style={{...s.td,color:T.buy,fontWeight:500}}>{fmtQty(pos.buy_qty)}</td>
                              <td style={{...s.td,color:T.sell,fontWeight:500}}>{fmtQty(pos.sell_qty)}</td>
                              <td style={s.td}><QtyDisplay v={pos.net_qty}/></td>
                              <td style={s.td}><ExchBadge exchange={pos.exchange} segment={pos.segment}/></td>
                              <td style={s.td}>{dayEquityActionFor(pos)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  )}
                </>)}
                  </>);
                })()}
                {dayOptions.length>0&&(<>
                  <SectionHdr title="Options" badge="F&O · View only" count={dayOptions.length}/>
                  {isMobile ? (
                    <div>
                      {dayOptions.map((pos,i)=>(
                        <MobileFOCard key={`mdayopt_${i}`}
                          symbol={pos.symbol} strikePrice={pos.strike_price} expiryDate={pos.expiry_date}
                          optionType={pos.option_type} exchange={pos.exchange} segment={pos.segment}
                          buyQty={pos.buy_qty} sellQty={pos.sell_qty} netQty={pos.net_qty} action={null}/>
                      ))}
                    </div>
                  ) : (
                  <div style={s.tcard}>
                    <div style={{overflowX:'auto'}}>
                      <table style={{width:'100%',borderCollapse:'collapse',minWidth:560}}>
                        <thead><tr>{['Symbol & strike','Expiry','Type','Buy qty','Sell qty','Net qty','Exchange'].map(h=><th key={h} style={s.th}>{h}</th>)}</tr></thead>
                        <tbody>
                          {dayOptions.map((pos,i)=>(
                            <tr key={i} style={{borderBottom:`0.5px solid ${T.bdr}`}}>
                              <td style={s.td}><div style={{fontWeight:600}}>{pos.symbol}</div>{pos.strike_price&&<div style={{fontSize:11,color:T.tx2}}>{formatStrikeLabel(pos.symbol,pos.strike_price)}</div>}</td>
                              <td style={{...s.td,fontSize:12,color:T.tx2}}>{formatExpiry(pos.expiry_date)}</td>
                              <td style={s.td}><span style={s.typePill(pos.option_type)}>{pos.option_type||'—'}</span></td>
                              <td style={{...s.td,color:T.buy,fontWeight:500}}>{fmtQty(pos.buy_qty)}</td>
                              <td style={{...s.td,color:T.sell,fontWeight:500}}>{fmtQty(pos.sell_qty)}</td>
                              <td style={s.td}><QtyDisplay v={pos.net_qty}/></td>
                              <td style={s.td}><ExchBadge exchange={pos.exchange} segment={pos.segment}/></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  )}
                </>)}
                {dayFutures.length>0&&(<>
                  <SectionHdr title="Futures" badge="F&O · View only" count={dayFutures.length}/>
                  {isMobile ? (
                    <div>
                      {dayFutures.map((pos,i)=>(
                        <MobileFOCard key={`mdayfut_${i}`}
                          symbol={pos.symbol} expiryDate={pos.expiry_date} optionType="FUT"
                          exchange={pos.exchange} segment={pos.segment}
                          buyQty={pos.buy_qty} sellQty={pos.sell_qty} netQty={pos.net_qty} action={null}/>
                      ))}
                    </div>
                  ) : (
                  <div style={s.tcard}>
                    <div style={{overflowX:'auto'}}>
                      <table style={{width:'100%',borderCollapse:'collapse',minWidth:480}}>
                        <thead><tr>{['Symbol','Expiry','Buy qty','Sell qty','Net qty','Exchange'].map(h=><th key={h} style={s.th}>{h}</th>)}</tr></thead>
                        <tbody>
                          {dayFutures.map((pos,i)=>(
                            <tr key={i} style={{borderBottom:`0.5px solid ${T.bdr}`}}>
                              <td style={{...s.td,fontWeight:600}}>{pos.symbol}</td>
                              <td style={{...s.td,fontSize:12,color:T.tx2}}>{formatExpiry(pos.expiry_date)}</td>
                              <td style={{...s.td,color:T.buy,fontWeight:500}}>{fmtQty(pos.buy_qty)}</td>
                              <td style={{...s.td,color:T.sell,fontWeight:500}}>{fmtQty(pos.sell_qty)}</td>
                              <td style={s.td}><QtyDisplay v={pos.net_qty}/></td>
                              <td style={s.td}><ExchBadge exchange={pos.exchange} segment={pos.segment}/></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  )}
                </>)}
              </>
            }
          </div>
        )}

        {/* ── B/F TAB ── */}
        {activeTab==='bf' && (
          <div className="nb-fade">
            {bfLoading?<div style={s.emptyState}>Loading B/F positions…</div>
            :!bfData||(bfOptions.length===0&&bfFutures.length===0)?<div style={s.emptyState}>No B/F positions found.</div>
            :<>
                <div style={s.sumBar}>
                  {[{l:'Total open',v:bfCount,c:T.brand},{l:'Positions',v:bfData.total_positions??0,c:T.tx},{l:'Options',v:bfOptions.length,c:'#7c3aed'},{l:'Futures',v:bfFutures.length,c:T.ok}].map((item,i)=>(
                    <div key={i} style={s.sumItem}><div style={s.sumLbl}>{item.l}</div><div style={s.sumVal(item.c)}>{item.v}</div></div>
                  ))}
                  <div style={{...s.sumItem,marginLeft:'auto'}}><div style={s.vonly}>View only — no square-off</div></div>
                </div>
                {bfOptions.length>0&&(<>
                  <SectionHdr title="Options" count={bfOptions.length}/>
                  {isMobile ? (
                    <div>
                      {bfOptions.map(pos=>{
                        const netLots = pos.display_qty??pos.qty_in_lots??pos.total_open_qty??(Number(pos.opng_lng_qty||0)-Number(pos.opng_shrt_qty||0));
                        return (
                          <MobileFOCard key={pos.bf_id}
                            symbol={pos.symbol} strikePrice={pos.strike_price} expiryDate={pos.expiry_date}
                            optionType={pos.option_type} exchange={pos.exchange} segment={pos.segment}
                            buyQty={Number(pos.opng_lng_qty||0)} sellQty={Number(pos.opng_shrt_qty||0)} netQty={netLots} action={null}/>
                        );
                      })}
                    </div>
                  ) : (
                  <div style={s.tcard}>
                    <div style={{overflowX:'auto'}}>
                      <table style={{width:'100%',borderCollapse:'collapse',minWidth:480}}>
                        <thead><tr>{['Symbol & strike','Expiry','Type','Net qty','Exchange'].map(h=><th key={h} style={s.th}>{h}</th>)}</tr></thead>
                        <tbody>
                          {bfOptions.map(pos=>(
                            <tr key={pos.bf_id} style={{borderBottom:`0.5px solid ${T.bdr}`}}>
                              <td style={s.td}><div style={{fontWeight:600}}>{pos.symbol}</div>{pos.strike_price&&<div style={{fontSize:11,color:T.tx2}}>{formatStrikeLabel(pos.symbol,pos.strike_price)}</div>}</td>
                              <td style={{...s.td,fontSize:12,color:T.tx2}}>{formatExpiry(pos.expiry_date)}</td>
                              <td style={s.td}><span style={s.typePill(pos.option_type)}>{pos.option_type}</span></td>
                              <td style={s.td}><QtyDisplay v={pos.display_qty??pos.qty_in_lots??pos.total_open_qty??(Number(pos.opng_lng_qty||0)-Number(pos.opng_shrt_qty||0))}/></td>
                              <td style={s.td}><ExchBadge exchange={pos.exchange} segment={pos.segment}/></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  )}
                </>)}
                {bfFutures.length>0&&(<>
                  <SectionHdr title="Futures" count={bfFutures.length}/>
                  {isMobile ? (
                    <div>
                      {bfFutures.map(pos=>{
                        const netLots = pos.display_qty??pos.qty_in_lots??pos.total_open_qty??(Number(pos.opng_lng_qty||0)-Number(pos.opng_shrt_qty||0));
                        return (
                          <MobileFOCard key={pos.bf_id}
                            symbol={pos.symbol} expiryDate={pos.expiry_date} optionType="FUT"
                            exchange={pos.exchange} segment={pos.segment}
                            buyQty={Number(pos.opng_lng_qty||0)} sellQty={Number(pos.opng_shrt_qty||0)} netQty={netLots} action={null}/>
                        );
                      })}
                    </div>
                  ) : (
                  <div style={s.tcard}>
                    <div style={{overflowX:'auto'}}>
                      <table style={{width:'100%',borderCollapse:'collapse',minWidth:400}}>
                        <thead><tr>{['Symbol','Expiry','Net qty','Exchange'].map(h=><th key={h} style={s.th}>{h}</th>)}</tr></thead>
                        <tbody>
                          {bfFutures.map(pos=>(
                            <tr key={pos.bf_id} style={{borderBottom:`0.5px solid ${T.bdr}`}}>
                              <td style={{...s.td,fontWeight:600}}>{pos.symbol}</td>
                              <td style={{...s.td,fontSize:12,color:T.tx2}}>{formatExpiry(pos.expiry_date)}</td>
                              <td style={s.td}><QtyDisplay v={pos.display_qty??pos.qty_in_lots??pos.total_open_qty??(Number(pos.opng_lng_qty||0)-Number(pos.opng_shrt_qty||0))}/></td>
                              <td style={s.td}><ExchBadge exchange={pos.exchange} segment={pos.segment}/></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  )}
                </>)}
              </>
            }
          </div>
        )}

        {/* ── HOLDINGS TAB ── */}
        {activeTab==='holdings' && (
          <div className="nb-fade">
            {holdingsLoading?<div style={s.emptyState}>Loading holdings…</div>
            :!holdingsData||holdingsData.holdings?.length===0?<div style={s.emptyState}>{holdingsData?.message||'No holdings found.'}</div>
            :<>
                <div style={s.sumBar}>
                  {[{l:'Securities',v:holdingsData.total_holdings,c:T.brand},{l:'Quantity',v:holdingsData.total_quantity,c:T.tx},{l:'Total value',v:'₹'+Number(holdingsData.total_value).toLocaleString('en-IN',{maximumFractionDigits:0}),c:T.ok}].map((item,i)=>(
                    <div key={i} style={s.sumItem}><div style={s.sumLbl}>{item.l}</div><div style={s.sumVal(item.c)}>{item.v}</div></div>
                  ))}
                  <div style={{...s.sumItem,marginLeft:'auto'}}><div style={{...s.vonly,background:T.okBg,borderColor:T.ok,color:T.okTx}}>NSDL · as of {holdingsData.date}</div></div>
                </div>
                {isMobile ? (
                  <div>
                    {holdingsData.holdings.map(h=>(
                      <MobileHoldingsCard key={h.id} h={h} action={holdingsActionFor(h)}/>
                    ))}
                  </div>
                ) : (
                <div style={s.tcard}>
                  <div style={{overflowX:'auto'}}>
                    <table style={{width:'100%',borderCollapse:'collapse',minWidth:520}}>
                      <thead><tr>{['Company','Qty','Close price','Value','Action'].map(h=><th key={h} style={s.th}>{h}</th>)}</tr></thead>
                      <tbody>
                        {holdingsData.holdings.map(h=>(
                          <tr key={h.id} style={{borderBottom:`0.5px solid ${T.bdr}`}}>
                            <td style={{...s.td,fontWeight:600,fontSize:fs-1}}>{h.company_name}</td>
                            <td style={{...s.td,fontWeight:500}}>{h.quantity}</td>
                            <td style={{...s.td,color:T.tx2}}>₹{Number(h.close_price).toLocaleString('en-IN',{maximumFractionDigits:2})}</td>
                            <td style={{...s.td,color:T.ok,fontWeight:500}}>₹{Number(h.total_value).toLocaleString('en-IN',{maximumFractionDigits:0})}</td>
                            <td style={s.td}>{holdingsActionFor(h)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                )}
              </>
            }
          </div>
        )}

        {/* ── ORDERS TAB ── */}
        {activeTab==='orders' && (
          <div className="nb-fade">
            {(() => {
              // Use pre-computed clientVisibleOrders (same filter as tab count)
              const clientOrders = clientVisibleOrders;
              const fmtDateTime = (dt) => {
                if (!dt) return '—';
                const d = new Date(dt);
                const date = d.toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'});
                const time = d.toLocaleTimeString('en-IN',{hour:'2-digit',minute:'2-digit',second:'2-digit',hour12:true});
                return `${date}, ${time}`;
              };
              if (clientOrders.length===0) return (
                <div style={s.emptyState}>No orders placed yet in this session.</div>
              );
              if (isMobile) return (
                <div>
                  {clientOrders.map((ord,i)=>(
                    <MobileOrderCard key={ord.order_id||i} ord={ord} label={orderStatusLabel(ord.status, ord.is_stale)}/>
                  ))}
                </div>
              );
              return (
                <div style={s.tcard}>
                  <div style={{overflowX:'auto'}}>
                    <table style={{width:'100%',borderCollapse:'collapse',minWidth:940}}>
                      <thead><tr>{['Symbol','Exchange','Type','Placed Qty','Traded Qty','Traded Price','Status','Placed By','Placed On','Traded On'].map(h=><th key={h} style={s.th}>{h}</th>)}</tr></thead>
                      <tbody>
                        {clientOrders.map((ord,i)=>{
                          const st=orderStatusLabel(ord.status, ord.is_stale);
                          const placedByLabel = (ord.placed_by||'CLIENT') + (ord.dealer_id ? ` (${ord.dealer_id})` : '');
                          const tradedTimeFmt = ord.traded_at ? new Date(ord.traded_at).toLocaleTimeString('en-IN',{hour:'2-digit',minute:'2-digit',second:'2-digit',hour12:true}) : '—';
                          return(<tr key={ord.order_id||i} style={{borderBottom:`0.5px solid ${T.bdr}`}}>
                            <td style={{...s.td,fontWeight:600,maxWidth:200}}>
                              {ord.strike_price ? formatStrikeLabel(ord.symbol, ord.strike_price) : ord.symbol}
                              {ord.strike_price && ord.option_type && <span style={{fontSize:10,color:T.tx2,marginLeft:4}}>{ord.option_type}</span>}
                              {ord.expiry_date && <span style={{fontSize:10,color:T.tx2,marginLeft:4}}>· {new Date(ord.expiry_date).toLocaleDateString('en-IN',{day:'2-digit',month:'short'})}</span>}
                            </td>
                            <td style={s.td}><ExchBadge exchange={ord.exchange} segment={ord.segment}/></td>
                            <td style={{...s.td,color:ord.side==='SELL'?T.sell:T.buy,fontWeight:600}}>{ord.side}</td>
                            <td style={s.td}>{ord.quantity}</td>
                            <td style={s.td}>{ord.traded_at ? (ord.executed_qty ?? '—') : '—'}</td>
                            <td style={s.td}>{ord.traded_at ? fmtPrice(ord.trade_price) : '—'}</td>
                            <td style={s.td}>
                              <span style={{background:st.bg,color:st.col,border:`0.5px solid ${st.col}`,borderRadius:6,fontSize:10,fontWeight:600,padding:'3px 9px',whiteSpace:'nowrap',display:'inline-block',minWidth:110,textAlign:'center'}}>
                                {st.label}
                              </span>
                            </td>
                            <td style={{...s.td,color:T.tx2,fontSize:11,whiteSpace:'nowrap'}}>{placedByLabel}</td>
                            <td style={{...s.td,color:T.tx2,fontSize:11,whiteSpace:'nowrap'}}>{fmtDateTime(ord.placed_at||ord.created_at)}</td>
                            <td style={{...s.td,color:T.tx2,fontSize:11,whiteSpace:'nowrap'}}>{tradedTimeFmt}</td>
                          </tr>);
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        <footer style={s.footer}>
          Refreshes every 60 seconds &nbsp;·&nbsp;
          <a href="mailto:support@navia.co.in" style={s.footerLnk}>support@navia.co.in</a>
          &nbsp;·&nbsp;
          <a href="https://support.navia.co.in" target="_blank" rel="noreferrer" style={s.footerLnk}>support.navia.co.in</a>
          &nbsp;·&nbsp; Navia Markets Ltd · SEBI registered
        </footer>
      </main>
    </div>
  );
}